import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import html2canvas from 'html2canvas';
import DemoBookingModal from '../components/DemoBookingModal';

const LOGO_URL = 'https://customer-assets.emergentagent.com/job_b71b88a0-de31-4e82-b047-c99df2477168/artifacts/7dlw52ts_logo%20%281%29.jpg';
const HERO_BG = 'https://customer-assets.emergentagent.com/job_7fc4408d-deea-4514-b797-0a32a1d11f55/artifacts/ch7afdat_Gemini_Generated_Image_ylp2j1ylp2j1ylp2.png';
const BEFORE_IMG = 'https://customer-assets.emergentagent.com/job_7fc4408d-deea-4514-b797-0a32a1d11f55/artifacts/kgwjo05z_Gemini_Generated_Image_j2s0owj2s0owj2s0.png';
const AFTER_IMG = 'https://customer-assets.emergentagent.com/job_7fc4408d-deea-4514-b797-0a32a1d11f55/artifacts/htyqv46f_Gemini_Generated_Image_dc9r9ddc9r9ddc9r.png';

// Dummy Schools for Live Demo
const DEMO_SCHOOLS = [
  {
    name: 'Delhi Public Academy', city: 'Lucknow', rating: 4.6, fees: '₹45,000/yr',
    image: 'https://images.unsplash.com/photo-1599666882726-fe28581e3147?w=600&h=400&fit=crop',
    facilities: ['Smart Classes', 'Sports Ground', 'Computer Lab', 'Library'],
    match: 94, students: 1200, seatsTotal: 40, seatsFilled: 34,
  },
  {
    name: 'Sunrise International School', city: 'Kanpur', rating: 4.3, fees: '₹38,000/yr',
    image: 'https://images.unsplash.com/photo-1687656532568-7d1862eaaa2b?w=600&h=400&fit=crop',
    facilities: ['AC Rooms', 'Swimming Pool', 'Dance Room', 'Science Lab'],
    match: 87, students: 800, seatsTotal: 35, seatsFilled: 29,
  },
  {
    name: 'St. Mary\'s Convent', city: 'Varanasi', rating: 4.8, fees: '₹52,000/yr',
    image: 'https://images.unsplash.com/photo-1688724740479-082da10d0bac?w=600&h=400&fit=crop',
    facilities: ['Auditorium', 'Playground', 'Music Room', 'Art Studio'],
    match: 91, students: 1500, seatsTotal: 45, seatsFilled: 41,
  },
  {
    name: 'Green Valley Public School', city: 'Agra', rating: 4.1, fees: '₹32,000/yr',
    image: 'https://images.unsplash.com/photo-1694307771413-ab92ba77539b?w=600&h=400&fit=crop',
    facilities: ['Transport', 'Canteen', 'Medical Room', 'CCTV'],
    match: 82, students: 600, seatsTotal: 30, seatsFilled: 22,
  },
];

// 8 Core Features - Principal को समझ आने वाली भाषा में
const ENGINE_FEATURES = [
  {
    num: 1, icon: '💘', color: 'from-pink-500 to-rose-600',
    title: { en: 'School Match', hi: 'School Match' },
    subtitle: { en: 'Parents Find Your School Automatically', hi: 'Parents अपने आप आपका School ढूंढ लें' },
    benefit: { en: 'Parents search for schools like they search on Swiggy — your school appears automatically with fees, seats & reviews. You get their phone number & budget directly.', hi: 'जैसे Swiggy पर restaurant ढूंढते हैं, वैसे ही parents school ढूंढें। आपका school अपने आप दिखे — fees, seats, reviews सब के साथ। Parent का phone number और budget सीधा आपको मिले।' },
    schoolGets: { en: 'Parent phone number, budget, class needed — ready-to-call leads', hi: 'Parent का phone number, budget, class — तुरंत call करने लायक leads' },
    parentGets: { en: 'Nearby schools with fees, seats & real info — 100% FREE', hi: 'पास के schools fees, seats और real info के साथ — 100% FREE' },
  },
  {
    num: 2, icon: '🔥', color: 'from-red-500 to-orange-600',
    title: { en: 'Live Seat Counter', hi: 'Live Seat Counter' },
    subtitle: { en: 'Like IRCTC — Seats Kam Dikhein, Parents Jaldi Aayein', hi: 'IRCTC जैसा — Seats कम दिखें, Parents जल्दी आएं' },
    benefit: { en: 'Just like IRCTC shows "Only 2 seats left" and you book immediately — parents see live seats filling up and rush to admit. No more "We will think about it".', hi: 'जैसे IRCTC में "सिर्फ 2 seats बची" दिखता है तो तुरंत book करते हैं — वैसे ही parents को seats भरती दिखें तो जल्दी admission ले लें। अब "सोचकर बताते हैं" नहीं बोलेंगे।' },
    schoolGets: { en: 'Parents decide 3x faster — "Only 5 seats left" creates real urgency', hi: 'Parents 3 गुना जल्दी decide करें — "सिर्फ 5 seats बची" से real urgency बने' },
    parentGets: { en: 'Real seat availability — no fake promises, waitlist option', hi: 'असली seat availability — कोई झूठा वादा नहीं, waitlist का option' },
  },
  {
    num: 3, icon: '📍', color: 'from-green-500 to-emerald-600',
    title: { en: 'Nearby Parents को Auto WhatsApp', hi: 'आस-पास के Parents को Auto WhatsApp' },
    subtitle: { en: 'No Pamphlet, No Banner — Direct WhatsApp', hi: 'पम्फलेट नहीं, बैनर नहीं — सीधा WhatsApp' },
    benefit: { en: 'Parents living near your school automatically get a WhatsApp message about admission — they walk in without you spending a single rupee on ads or pamphlets.', hi: 'जो parents आपके school के पास रहते हैं, उन्हें automatic WhatsApp message चला जाए admission का। बिना एक रुपया खर्च किए parents चलकर आएं — न pamphlet, न banner, न ad।' },
    schoolGets: { en: 'Parents come walking in — zero pamphlet/banner cost', hi: 'Parents चलकर आएं — pamphlet/banner का खर्चा zero' },
    parentGets: { en: 'Nearby school info with special offers on WhatsApp', hi: 'पास के school की जानकारी WhatsApp पर special offer के साथ' },
  },
  {
    num: 4, icon: '🤖', color: 'from-blue-500 to-indigo-600',
    title: { en: 'AI Calling Bot', hi: 'AI फ़ोन करने वाला Robot' },
    subtitle: { en: 'Robot Makes 100+ Calls Daily — Staff Free', hi: 'Robot रोज़ 100+ Calls करे — Staff Free रहे' },
    benefit: { en: 'AI robot calls 100+ parents daily in Hindi/English, gives school info, books visit — all for just ₹2-3 per call. Your staff does not have to sit on the phone all day.', hi: 'AI robot रोज़ 100+ parents को call करे Hindi/English में, school की जानकारी दे, visit book करे — सिर्फ ₹2-3 per call। आपके staff को दिनभर phone पर बैठने की ज़रूरत नहीं।' },
    schoolGets: { en: 'Robot calls 100+ parents/day — recordings available, staff free', hi: 'Robot रोज़ 100+ calls करे — recording मिले, staff free रहे' },
    parentGets: { en: 'Quick school info, polite conversation, easy visit booking', hi: 'School की जानकारी जल्दी, अच्छी बात, आसान visit booking' },
  },
  {
    num: 5, icon: '🎮', color: 'from-purple-500 to-violet-600',
    title: { en: 'AI Entrance Test (Game-based)', hi: 'AI से Entrance Test (Game जैसा)' },
    subtitle: { en: 'No Exam Stress — Fun Games', hi: 'Exam का Tension नहीं — Game खेलो, Result पाओ' },
    benefit: { en: 'Instead of boring written exams, children play fun games on phone — AI checks their memory, logic, creativity. Parents get a detailed report card. Your school looks very advanced & modern.', hi: 'बोरिंग written exam की जगह बच्चे phone पर game खेलें — AI उनकी memory, logic, creativity check करे। Parents को detailed report card मिले। आपका school बहुत advanced और modern दिखे।' },
    schoolGets: { en: 'School looks modern & advanced — detailed child profile for better class placement', hi: 'School modern और advanced दिखे — बच्चे की पूरी profile मिले class placement के लिए' },
    parentGets: { en: 'No exam tension for child — fun experience, detailed ability report', hi: 'बच्चे को exam का tension नहीं — game जैसा experience, detailed report' },
  },
  {
    num: 6, icon: '🤝', color: 'from-amber-500 to-yellow-600',
    title: { en: 'AI Fee Negotiator', hi: 'AI से Fees बात करे' },
    subtitle: { en: 'No Awkward Fee Discussion — AI Handles It', hi: 'Fees की अजीब बातचीत नहीं — AI Handle करे' },
    benefit: { en: 'Parents always bargain on fees. Now AI negotiates via WhatsApp — finds the best deal for both sides. Your staff does not have to get into uncomfortable fee discussions.', hi: 'Parents हमेशा fees में मोलभाव करते हैं। अब AI WhatsApp पर fees negotiate करे — दोनों sides के लिए best deal निकाले। आपके staff को fees की अजीब बातचीत नहीं करनी पड़ेगी।' },
    schoolGets: { en: 'Maximum revenue per admission — AI finds best price automatically', hi: 'हर admission पर maximum revenue — AI अपने आप best price निकाले' },
    parentGets: { en: 'Best possible fee, clear options, no pressure', hi: 'Best possible fees, clear options, कोई pressure नहीं' },
  },
  {
    num: 7, icon: '💳', color: 'from-teal-500 to-cyan-600',
    title: { en: 'Padhai EMI', hi: 'पढ़ाई EMI' },
    subtitle: { en: 'Full Fees Upfront to You — Parents Pay Monthly', hi: 'आपको पूरी Fees एक साथ — Parents Monthly भरें' },
    benefit: { en: 'NBFC (finance company) pays you the FULL yearly fees upfront on Day 1. Parents pay easy monthly EMIs to the NBFC. No more fee defaults, no more collection stress.', hi: 'NBFC (finance company) आपको Day 1 पर पूरी साल की fees एक साथ दे दे। Parents NBFC को आसान monthly EMI भरें। अब fees डूबने का tension नहीं, collection का stress नहीं।' },
    schoolGets: { en: 'FULL FEES DAY 1 (NBFC pays you) — zero fee defaults, guaranteed cash flow', hi: 'पूरी FEES DAY 1 (NBFC दे) — fees डूबने का risk zero, cash flow guaranteed' },
    parentGets: { en: 'Easy monthly EMI, 0% interest, auto-debit', hi: 'आसान monthly EMI, 0% ब्याज, auto-debit' },
  },
  {
    num: 8, icon: '🎬', color: 'from-indigo-500 to-blue-600',
    title: { en: 'Principal Welcome Video', hi: 'Principal का Welcome Video' },
    subtitle: { en: 'Parents Share, School Goes Viral', hi: 'Parents Share करें, School Viral हो जाए' },
    benefit: { en: 'Record one welcome video — AI creates a personalized version with each student\'s name. Parents feel "Wow, principal knows my child!" and share the video everywhere — FREE marketing!', hi: 'एक बार welcome video record करें — AI हर बच्चे के नाम से personalized version बना दे। Parents feel करें "वाह, principal को मेरे बच्चे का नाम पता है!" और video सबको share करें — FREE marketing!' },
    schoolGets: { en: 'Wow factor — parents share video everywhere, free viral marketing', hi: 'Wow factor — parents video share करें, free viral marketing' },
    parentGets: { en: 'Personal welcome from principal — feels special, shareable', hi: 'Principal का personal welcome — special feel, share करने लायक' },
  },
];

// Admission Problems — Principal की Daily ज़िंदगी
const ADMISSION_PROBLEMS = {
  hi: [
    { icon: '📞', title: 'Staff दिनभर Phone पर बैठा', desc: 'Staff सुबह से शाम तक parents को call करता है — फिर भी 70% parents "सोचकर बताते हैं" बोलकर गायब हो जाते हैं' },
    { icon: '📝', title: 'Register में Entry, Excel में Data', desc: 'कितने inquiry आई, कितने visit हुए, कितने admission हुए — सब अलग-अलग register और Excel में। कुछ ढूंढना हो तो घंटे लग जाएं' },
    { icon: '💸', title: 'Fees समय पर नहीं आती', desc: 'Parents fees देने में 2-3 महीने delay करते हैं। School का cash flow रुक जाता है — salary, bills सब अटक जाते हैं' },
    { icon: '📉', title: '100 Inquiry में 10-15 Admission', desc: '100 parents enquiry करते हैं लेकिन admission सिर्फ 10-15 होते हैं। बाकी 85 parents कहाँ गए — कोई track नहीं' },
    { icon: '🏃', title: 'Slow Response = Lost Admission', desc: 'Parent ने enquiry की, आपने 2 दिन बाद call किया — तब तक parent ने दूसरे school में admission करा लिया। Competition में देर = हार' },
    { icon: '💰', title: 'Pamphlet और Banner पर लाखों खर्च', desc: 'हर साल pamphlets, banners, newspaper ads पर 2-5 लाख खर्च — लेकिन कितने admission इनसे आए, कोई नहीं जानता' },
  ],
  en: [
    { icon: '📞', title: 'Staff on Phone All Day', desc: 'Staff calls parents from morning to evening — still 70% parents say "will think about it" and disappear' },
    { icon: '📝', title: 'Registers, Excel, Chaos', desc: 'Inquiries, visits, admissions — all in different registers and Excel sheets. Finding anything takes hours' },
    { icon: '💸', title: 'Fees Come Late', desc: 'Parents delay fees by 2-3 months. School cash flow stops — salary, bills all get stuck' },
    { icon: '📉', title: '100 Inquiries = 10-15 Admissions', desc: '100 parents inquire but only 10-15 admit. Where did the other 85 go? No tracking at all' },
    { icon: '🏃', title: 'Slow Response = Lost Admission', desc: 'Parent inquired, you called back 2 days later — they already admitted in another school' },
    { icon: '💰', title: 'Lakhs Spent on Pamphlets & Banners', desc: 'Every year ₹2-5 lakhs on pamphlets, banners, newspaper ads — but no one knows how many admissions came from them' },
  ]
};

export default function AdmissionEngineLandingPage() {
  const { language } = useLanguage();
  const [lang, setLang] = useState(language || 'hi');
  const [expandedFeature, setExpandedFeature] = useState(null);
  const [currentSchool, setCurrentSchool] = useState(0);
  const [swipeAnim, setSwipeAnim] = useState(null);
  const [showMatch, setShowMatch] = useState(false);
  const [likedSchools, setLikedSchools] = useState([]);
  const [seatAnim, setSeatAnim] = useState(false);
  const [showDemoModal, setShowDemoModal] = useState(false);

  // Child Name for personalized result
  const [childName, setChildName] = useState('');
  const [nameStep, setNameStep] = useState(false); // show name input before game

  // Live Visitor Counter (fake animated)
  const [liveVisitors, setLiveVisitors] = useState(43);

  // Testimonial Toasts
  const [currentToast, setCurrentToast] = useState(0);
  const [showToast, setShowToast] = useState(false);

  const testimonials = [
    { name: 'Sharma Sir', city: 'Lucknow', text: { en: 'Got 12 new admission leads today!', hi: 'आज 12 नए admission leads मिले!' } },
    { name: 'Verma Sir', city: 'Kanpur', text: { en: 'Seats filling up fast with Live Counter!', hi: 'Live Counter से seats तेज़ी से भर रही हैं!' } },
    { name: 'DPS Lucknow', city: 'Lucknow', text: { en: '40 seats filled in just 2 weeks!', hi: '2 हफ्ते में 40 seats भर गईं!' } },
    { name: 'Gupta Madam', city: 'Agra', text: { en: 'Parents sharing welcome video everywhere!', hi: 'Parents welcome video हर जगह share कर रहे!' } },
    { name: 'Patel Sir', city: 'Jaipur', text: { en: 'NBFC paid full fees on Day 1!', hi: 'NBFC ने Day 1 पर पूरी fees दे दी!' } },
    { name: 'Sister Agnes', city: 'Varanasi', text: { en: 'Zero fee defaults this year!', hi: 'इस साल एक भी fee नहीं डूबी!' } },
  ];

  // FAQ data — Principal के सवाल
  const faqData = [
    { q: { en: 'How much does it cost for my school?', hi: 'School के लिए इसका कितना खर्चा आएगा?' }, a: { en: 'We have plans starting from ₹5,000/month. Free demo and setup included. You start paying only when you see results.', hi: 'Plans ₹5,000/month से शुरू हैं। Free demo और setup शामिल है। जब तक result न दिखे, तब तक भुगतान नहीं।' } },
    { q: { en: 'My staff is not tech-savvy. Will they manage?', hi: 'मेरा staff technology नहीं जानता। चला पाएगा?' }, a: { en: 'Everything works on WhatsApp — the app your staff already uses daily. Our team gives full training. Most schools go live in 1 day.', hi: 'सब WhatsApp पर चलता है — जो आपका staff रोज़ use करता है। हमारी team पूरी training देती है। ज़्यादातर schools 1 दिन में live हो जाते हैं।' } },
    { q: { en: 'Is our school data safe? Government rules?', hi: 'School का data safe रहेगा? Government rules?' }, a: { en: 'Bank-level security. Regular backups. Your data is never shared with anyone. Fully compliant with Indian data protection laws.', hi: 'Bank level security। Regular backups। आपका data किसी से share नहीं होता। भारत के सभी data protection laws का पालन।' } },
    { q: { en: 'What if parents do not use apps?', hi: 'Parents अगर app नहीं चलाते तो?' }, a: { en: 'No app needed! Everything works on WhatsApp. AI bot calls parents directly. Parents just need a phone — smartphone not even required for calls.', hi: 'कोई app ज़रूरी नहीं! सब WhatsApp पर चलता है। AI bot parents को खुद call करता है। Parents को बस phone चाहिए — smartphone भी ज़रूरी नहीं calls के लिए।' } },
    { q: { en: 'Padhai EMI — how does the school get full fees?', hi: 'पढ़ाई EMI — school को पूरी fees कैसे मिलेगी?' }, a: { en: 'NBFC (like a bank) pays your school the full annual fees on Day 1. Parents pay monthly installments to NBFC. If parent defaults, NBFC bears the loss — not your school.', hi: 'NBFC (जैसे bank) आपके school को Day 1 पर पूरे साल की fees दे देता है। Parents NBFC को monthly किस्तें भरते हैं। अगर parent न भरे तो नुकसान NBFC का — आपके school का नहीं।' } },
    { q: { en: 'We are a small school. Is this for us?', hi: 'हमारा छोटा school है। क्या ये हमारे लिए है?' }, a: { en: 'This is MADE for small and medium schools! Big schools already have marketing teams. You don\'t — that\'s exactly why AI helps you compete with them.', hi: 'ये छोटे और मध्यम schools के लिए ही बना है! बड़े schools में marketing team होती है। आपके पास नहीं — इसलिए AI आपको उनसे compete करने में help करता है।' } },
  ];
  const [openFaq, setOpenFaq] = useState(null);

  // Success Stories Carousel
  const [storyIdx, setStoryIdx] = useState(0);
  const [storyPlaying, setStoryPlaying] = useState(null);
  const successStories = [
    {
      school: 'Delhi Public Academy', city: 'Lucknow', principal: 'Mrs. Sharma',
      image: 'https://images.unsplash.com/photo-1759143101324-d375443f1955?w=600&h=400&fit=crop',
      metric: { value: '3x', label: { en: 'More Admissions', hi: 'ज़्यादा Admissions' } },
      quote: { en: '"I was skeptical at first. But when 95% seats filled in 3 weeks — without a single pamphlet — I became a fan. The AI calling bot alone replaced 3 staff members."', hi: '"पहले मुझे भी शक था। लेकिन जब 3 हफ्ते में 95% seats भर गईं — बिना एक भी pamphlet के — तो मैं fan हो गई। AI calling bot ने अकेले 3 staff members का काम कर दिया।"' },
      stats: [
        { label: { en: 'Seats Filled', hi: 'Seats भरीं' }, value: '95%' },
        { label: { en: 'AI Calls Made', hi: 'AI ने Calls कीं' }, value: '500+' },
        { label: { en: 'Pamphlet Cost', hi: 'Pamphlet खर्चा' }, value: '₹0' },
      ],
      duration: '0:32',
    },
    {
      school: 'Sunrise International', city: 'Kanpur', principal: 'Mr. Verma',
      image: 'https://images.unsplash.com/photo-1759755486793-b4920faa6483?w=600&h=400&fit=crop',
      metric: { value: '5x', label: { en: 'More Inquiries', hi: 'ज़्यादा Inquiries' } },
      quote: { en: '"Earlier we used to get 20-30 inquiries per month. After School Match went live, we started getting 150+ inquiries. Parents who never heard of us are now walking in!"', hi: '"पहले महीने में 20-30 inquiry आती थीं। School Match live होने के बाद 150+ inquiries आने लगीं। जिन parents ने हमारा नाम नहीं सुना था, वो भी चलकर आने लगे!"' },
      stats: [
        { label: { en: 'New Inquiries', hi: 'नई Inquiries' }, value: '150+' },
        { label: { en: 'Walk-ins', hi: 'Walk-ins' }, value: '2x' },
        { label: { en: 'Time Saved', hi: 'Staff का Time बचा' }, value: '80%' },
      ],
      duration: '0:28',
    },
    {
      school: 'St. Mary\'s Convent', city: 'Varanasi', principal: 'Sister Agnes',
      image: 'https://images.unsplash.com/photo-1769201153045-98827f62996b?w=600&h=400&fit=crop',
      metric: { value: '100%', label: { en: 'Fees On Time', hi: 'Fees समय पर' } },
      quote: { en: '"Fee collection was our biggest headache. With Padhai EMI, NBFC pays us full fees on Day 1. Parents pay monthly. No more chasing parents for fees. Life is peaceful now."', hi: '"Fees collection सबसे बड़ा सिरदर्द था। पढ़ाई EMI से NBFC Day 1 पर पूरी fees दे देता है। Parents monthly भरते हैं। अब parents के पीछे भागना नहीं पड़ता। Life peaceful हो गई।"' },
      stats: [
        { label: { en: 'Fees On Day 1', hi: 'Day 1 पर Fees' }, value: '100%' },
        { label: { en: 'Fee Default', hi: 'Fees डूबी' }, value: '0%' },
        { label: { en: 'Happy Parents', hi: 'खुश Parents' }, value: '450+' },
      ],
      duration: '0:25',
    },
    {
      school: 'Green Valley School', city: 'Agra', principal: 'Dr. Gupta',
      image: 'https://images.unsplash.com/photo-1542810634-71277d95dcbb?w=600&h=400&fit=crop',
      metric: { value: '10x', label: { en: 'Brand Visibility', hi: 'School की पहचान' } },
      quote: { en: '"The personalized welcome video was a masterstroke. Parents shared it in every WhatsApp group. Our school became famous in the whole city — all for free!"', hi: '"Personalized welcome video ने तो कमाल कर दिया। Parents ने हर WhatsApp group में share किया। पूरे शहर में school famous हो गया — वो भी free में!"' },
      stats: [
        { label: { en: 'Videos Sent', hi: 'Videos भेजे' }, value: '200+' },
        { label: { en: 'WA Shares', hi: 'WA Shares' }, value: '1.2K' },
        { label: { en: 'Brand Boost', hi: 'पहचान बढ़ी' }, value: '10x' },
      ],
      duration: '0:30',
    },
    {
      school: 'Modern Academy', city: 'Jaipur', principal: 'Mrs. Patel',
      image: 'https://images.pexels.com/photos/32001514/pexels-photo-32001514.jpeg?auto=compress&cs=tinysrgb&w=600&h=400&fit=crop',
      metric: { value: '14 Days', label: { en: 'All Seats Filled', hi: 'सब Seats भरीं' } },
      quote: { en: '"Live Seat Counter was genius. When parents saw \'Only 5 seats left\', they stopped delaying. All seats filled in just 2 weeks. Even got 85 on the waitlist!"', hi: '"Live Seat Counter ने जादू कर दिया। जब parents ने देखा \'सिर्फ 5 seats बची\', तो सोचना बंद कर दिया। सिर्फ 2 हफ्ते में सब seats भर गईं। 85 waiting list में भी आ गए!"' },
      stats: [
        { label: { en: 'Time to Fill', hi: 'भरने में लगे' }, value: '14 दिन' },
        { label: { en: 'Waitlisted', hi: 'Waiting List' }, value: '85' },
        { label: { en: 'Conversion', hi: 'Admission Rate' }, value: '78%' },
      ],
      duration: '0:35',
    },
  ];

  // Auto-advance carousel
  useEffect(() => {
    if (storyPlaying !== null) return;
    const interval = setInterval(() => {
      setStoryIdx(prev => (prev + 1) % successStories.length);
    }, 5000);
    return () => clearInterval(interval);
  }, [storyPlaying]);

  // AI Assessment Game States
  const [activeGame, setActiveGame] = useState(null); // 'pattern' | 'memory' | null
  const [patternQ, setPatternQ] = useState(0);
  const [patternScore, setPatternScore] = useState(0);
  const [patternFeedback, setPatternFeedback] = useState(null);
  const [memoryCards, setMemoryCards] = useState([]);
  const [memoryFlipped, setMemoryFlipped] = useState([]);
  const [memoryMatched, setMemoryMatched] = useState([]);
  const [memoryMoves, setMemoryMoves] = useState(0);
  const [gameComplete, setGameComplete] = useState(false);

  // Pattern Game Data
  const patternQuestions = [
    { seq: ['🔴','🔵','🔴','🔵','🔴'], answer: '🔵', options: ['🔵','🟢','🔴','🟡'], hint: { en: 'Red, Blue pattern repeats...', hi: 'लाल, नीला pattern repeat हो रहा...' } },
    { seq: ['⭐','⭐','🌙','⭐','⭐'], answer: '🌙', options: ['⭐','🌙','☀️','🌟'], hint: { en: 'Star, Star, Moon pattern...', hi: 'Star, Star, Moon pattern...' } },
    { seq: ['1','2','3','4','5'], answer: '6', options: ['6','8','7','9'], hint: { en: 'Simple counting...', hi: 'Simple counting...' } },
    { seq: ['🐱','🐶','🐱','🐶','🐱'], answer: '🐶', options: ['🐱','🐶','🐰','🐸'], hint: { en: 'Cat, Dog alternating...', hi: 'Cat, Dog बारी-बारी...' } },
  ];

  // Memory Game Init
  const initMemoryGame = () => {
    const emojis = ['🎨','📚','⚽','🎵','🧩','🌈'];
    const pairs = [...emojis, ...emojis];
    const shuffled = pairs.sort(() => Math.random() - 0.5).map((emoji, i) => ({ id: i, emoji, flipped: false, matched: false }));
    setMemoryCards(shuffled);
    setMemoryFlipped([]);
    setMemoryMatched([]);
    setMemoryMoves(0);
    setGameComplete(false);
  };

  const handleMemoryClick = (idx) => {
    if (memoryFlipped.length === 2 || memoryFlipped.includes(idx) || memoryMatched.includes(idx)) return;
    const newFlipped = [...memoryFlipped, idx];
    setMemoryFlipped(newFlipped);
    if (newFlipped.length === 2) {
      setMemoryMoves(prev => prev + 1);
      if (memoryCards[newFlipped[0]].emoji === memoryCards[newFlipped[1]].emoji) {
        const newMatched = [...memoryMatched, newFlipped[0], newFlipped[1]];
        setMemoryMatched(newMatched);
        setMemoryFlipped([]);
        if (newMatched.length === memoryCards.length) setGameComplete(true);
      } else {
        setTimeout(() => setMemoryFlipped([]), 700);
      }
    }
  };

  const handlePatternAnswer = (ans) => {
    const correct = ans === patternQuestions[patternQ].answer;
    setPatternFeedback(correct ? 'correct' : 'wrong');
    if (correct) setPatternScore(prev => prev + 1);
    setTimeout(() => {
      setPatternFeedback(null);
      if (patternQ < patternQuestions.length - 1) {
        setPatternQ(prev => prev + 1);
      } else {
        setGameComplete(true);
      }
    }, 800);
  };

  const resetGames = () => {
    setActiveGame(null);
    setNameStep(false);
    setPatternQ(0);
    setPatternScore(0);
    setPatternFeedback(null);
    setGameComplete(false);
    setMemoryCards([]);
    setMemoryFlipped([]);
    setMemoryMatched([]);
    setMemoryMoves(0);
  };

  // Share Result Card
  const resultCardRef = useRef(null);
  const [shareDownloading, setShareDownloading] = useState(false);

  const downloadResultCard = async () => {
    if (!resultCardRef.current) return;
    setShareDownloading(true);
    try {
      const canvas = await html2canvas(resultCardRef.current, { backgroundColor: '#0f172a', scale: 2, useCORS: true });
      const link = document.createElement('a');
      link.download = `shiksha-mitra-assessment-${Date.now()}.png`;
      link.href = canvas.toDataURL('image/png');
      link.click();
    } catch (e) { console.error(e); }
    setShareDownloading(false);
  };

  const shareResultWhatsApp = (gameType) => {
    const score = gameType === 'pattern'
      ? `${patternScore}/${patternQuestions.length}`
      : `${memoryMoves} moves`;
    const gameName = gameType === 'pattern'
      ? t('Pattern Recognition', 'Pattern Recognition')
      : t('Memory Match', 'Memory Match');
    const analysis = gameType === 'pattern'
      ? (patternScore >= 3 ? t('Excellent!','बेहतरीन!') : patternScore >= 2 ? t('Good!','अच्छा!') : t('Creative!','Creative!'))
      : (memoryMoves <= 8 ? t('Outstanding!','शानदार!') : memoryMoves <= 12 ? t('Great!','अच्छा!') : t('Keep going!','Practice करें!'));
    const nameText = childName ? `${childName} ` : '';
    const text = t(
      `${nameText}scored ${score} in ${gameName} on Shiksha Mitra AI Assessment! AI says: ${analysis} Try it free:`,
      `${nameText}ने शिक्षा मित्र AI Assessment में ${gameName} में ${score} score किया! AI कहता है: ${analysis} Free try करें:`
    );
    window.open(`https://wa.me/?text=${encodeURIComponent(text + ' ' + shareUrl + '#live-demo')}`, '_blank');
  };

  const copyResultText = (gameType) => {
    const score = gameType === 'pattern'
      ? `${patternScore}/${patternQuestions.length}`
      : `${memoryMoves} moves`;
    const gameName = gameType === 'pattern' ? 'Pattern Recognition' : 'Memory Match';
    const nameText = childName ? `${childName}: ` : '';
    const text = `${nameText}${gameName}: ${score} | Shiksha Mitra AI Assessment | ${shareUrl}`;
    navigator.clipboard.writeText(text).catch(() => {});
  };

  useEffect(() => { window.scrollTo(0, 0); }, []);
  useEffect(() => { setLang(language); }, [language]);

  // Live Visitor Counter animation
  useEffect(() => {
    const interval = setInterval(() => {
      setLiveVisitors(prev => {
        const change = Math.random() > 0.5 ? 1 : -1;
        return Math.max(28, Math.min(67, prev + change));
      });
    }, 3000);
    return () => clearInterval(interval);
  }, []);

  // Testimonial toast rotation
  useEffect(() => {
    const interval = setInterval(() => {
      setShowToast(true);
      setTimeout(() => {
        setShowToast(false);
        setTimeout(() => setCurrentToast(prev => (prev + 1) % testimonials.length), 400);
      }, 4000);
    }, 6000);
    // Show first toast after 3s
    const firstTimeout = setTimeout(() => setShowToast(true), 3000);
    return () => { clearInterval(interval); clearTimeout(firstTimeout); };
  }, []);

  const shareUrl = `${window.location.origin}/admission`;
  const t = (en, hi) => lang === 'hi' ? hi : en;
  const scrollToSection = (id) => { document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' }); };

  // When an already-logged-in (demo) user opens this public page, the primary CTAs
  // become "Activate" (route into the app where DemoMode opens the activate modal)
  // instead of "Book a Free Demo".
  const isLoggedIn = typeof window !== 'undefined' && !!localStorage.getItem('token');
  const handleActivateOrDemo = () => {
    if (isLoggedIn) {
      window.location.href = '/dashboard?activate=1';
    } else {
      setShowDemoModal(true);
    }
  };
  // CTA label helper: returns "Activate" when logged in, else the marketing/demo label.
  const ctaLabel = (demoEn, demoHi) => (isLoggedIn ? t('Activate Now', 'अभी Activate करें') : t(demoEn, demoHi));

  const problems = ADMISSION_PROBLEMS[lang] || ADMISSION_PROBLEMS.hi;

  return (
    <div className="min-h-screen bg-slate-50 relative" data-testid="admission-engine-landing">
      {/* Background Grid Pattern */}
      <div className="fixed inset-0 z-0 opacity-30 pointer-events-none" style={{
        backgroundImage: 'radial-gradient(circle at 1px 1px, rgb(203 213 225 / 0.4) 1px, transparent 0)',
        backgroundSize: '40px 40px'
      }}></div>

      {/* Decorative Glow Orbs */}
      <div className="fixed top-40 left-20 w-96 h-96 bg-orange-400/10 rounded-full blur-[120px] z-0 pointer-events-none"></div>
      <div className="fixed bottom-40 right-20 w-80 h-80 bg-indigo-500/10 rounded-full blur-[100px] z-0 pointer-events-none"></div>

      {/* Navigation - Exact Match */}
      <nav className="fixed top-0 left-0 right-0 bg-white/80 backdrop-blur-xl border-b border-slate-200/50 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center gap-2">
              <img src={LOGO_URL} alt="Shiksha Mitra Logo" className="h-10 w-10 rounded-lg object-cover" />
              <Link to="/" className="text-xl font-bold bg-gradient-to-r from-orange-500 to-green-600 bg-clip-text text-transparent" style={{fontFamily: 'Outfit, sans-serif'}} data-testid="ae-header-logo">शिक्षा मित्र</Link>
            </div>
            <div className="hidden md:flex items-center gap-6">
              <button onClick={() => scrollToSection('features-overview')} className="text-slate-600 hover:text-orange-500 transition font-medium" style={{fontFamily: 'Manrope, sans-serif'}}>Features</button>
              <button onClick={() => scrollToSection('features-detail')} className="text-slate-600 hover:text-orange-500 transition font-medium" style={{fontFamily: 'Manrope, sans-serif'}}>{t('Details', 'विवरण')}</button>
              <Link to="/features" className="text-slate-600 hover:text-orange-500 transition font-medium" style={{fontFamily: 'Manrope, sans-serif'}}>{t('All 31 AI', 'सभी 31 AI')}</Link>
            </div>
            <div className="flex items-center gap-3">
              <button onClick={() => setLang(lang === 'hi' ? 'en' : 'hi')} className="flex items-center gap-1 px-3 py-1.5 rounded-full bg-slate-100 hover:bg-slate-200 transition text-sm font-medium">
                <span className={lang === 'en' ? 'font-bold text-orange-500' : 'text-slate-500'}>EN</span><span className="text-slate-300">|</span><span className={lang === 'hi' ? 'font-bold text-orange-500' : 'text-slate-500'}>हिं</span>
              </button>
              <Link to="/login" className="px-4 py-2 text-orange-500 hover:text-orange-600 font-semibold transition" style={{display: isLoggedIn ? 'none' : undefined}}>Login</Link>
              <button onClick={handleActivateOrDemo} className="px-5 py-2.5 bg-slate-900 text-white rounded-full hover:bg-slate-800 transition font-medium shadow-lg" data-testid="ae-cta-top">
                {ctaLabel('Start Free', 'अभी शुरू करें')}
              </button>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section - Floating Dark Island (Same as Main) */}
      <section className="pt-28 pb-8 px-4 md:px-8 relative z-10" data-testid="ae-hero">
        <div className="max-w-[95%] xl:max-w-7xl mx-auto">
          <div className="bg-slate-950 rounded-[2.5rem] md:rounded-[3rem] overflow-hidden relative shadow-2xl shadow-slate-900/30">
            {/* Background Image */}
            <div className="absolute inset-0 z-0" style={{
              backgroundImage: `url('${HERO_BG}')`,
              backgroundSize: 'cover', backgroundPosition: 'center', backgroundRepeat: 'no-repeat'
            }} />
            <div className="absolute inset-0 z-0 bg-gradient-to-b from-slate-950/80 via-slate-950/60 to-slate-950/90" />

            <div className="relative z-10 px-8 md:px-16 lg:px-24 py-16 md:py-24">
              <div className="text-center max-w-4xl mx-auto">
                {/* Badge */}
                <div className="inline-flex items-center gap-2 px-4 py-2 bg-white/10 backdrop-blur-md text-white border border-white/20 rounded-full text-sm font-medium mb-6" style={{fontFamily: 'Manrope, sans-serif'}}>
                  <span className="text-green-400">✅</span> {t('500+ Schools Already Using', '500+ Schools पहले से Use कर रहे हैं')}
                </div>
                {/* Live Visitor Counter */}
                <div className="flex items-center justify-center gap-2 mb-4" data-testid="live-visitor-counter">
                  <span className="w-2 h-2 bg-green-400 rounded-full animate-pulse"></span>
                  <span className="text-green-400/80 text-sm font-medium" style={{fontFamily: 'Manrope, sans-serif'}}>
                    {liveVisitors} {t('principals viewing this right now', 'principals अभी ये देख रहे हैं')}
                  </span>
                </div>

                {/* Title */}
                <h1 className="text-4xl sm:text-5xl md:text-7xl font-black mb-6 tracking-tight text-white" style={{fontFamily: 'Outfit, sans-serif', textShadow: '0 4px 30px rgba(0,0,0,0.5)'}} data-testid="ae-hero-title">
                  {t('Admission Season', 'Admission Season')}<br/>{t('Tension? Not Anymore!', 'का Tension? अब नहीं!')}
                </h1>

                {/* Subtitle */}
                <p className="text-lg md:text-2xl text-white/90 mb-4 font-medium" style={{fontFamily: 'Manrope, sans-serif'}}>
                  {t('From Parent Inquiry to Admission — Everything on Autopilot', 'Parent की Inquiry से Admission तक — सब Autopilot पर')}
                </p>

                {/* Description */}
                <p className="text-base md:text-lg text-white/70 mb-10 max-w-2xl mx-auto leading-relaxed" style={{fontFamily: 'Manrope, sans-serif'}}>
                  {t('8 AI tools that do the work of 10 staff members. Parents find your school, seats fill automatically, fees come on time. Your staff focuses on teaching, not phone calls.',
                     'AI के 8 हथियार जो 10 staff का काम करें। Parents अपने आप school ढूंढें, seats अपने आप भरें, fees समय पर आएं। आपका staff पढ़ाई पर ध्यान दे, phone पर नहीं।')}
                </p>

                {/* CTAs */}
                <div className="flex flex-col sm:flex-row gap-4 justify-center mb-16">
                  <button onClick={handleActivateOrDemo} className="px-8 py-4 bg-orange-500 text-white rounded-full hover:bg-orange-600 transition-all font-semibold text-lg shadow-xl shadow-orange-500/30 transform hover:scale-105 hover:-translate-y-1" data-testid="ae-hero-demo" style={{fontFamily: 'Manrope, sans-serif'}}>
                    {t('See How It Works', 'देखिए कैसे काम करता है')} →
                  </button>
                  <Link to="/admission-portal" className="px-8 py-4 bg-green-500 text-white rounded-full hover:bg-green-600 transition-all font-semibold text-lg shadow-xl shadow-green-500/30 transform hover:scale-105 hover:-translate-y-1 text-center" data-testid="ae-school-match" style={{fontFamily: 'Manrope, sans-serif'}}>
                    {isLoggedIn ? t('Activate Now', 'अभी Activate करें') : t('Parent Portal Demo', 'Parent वाला Portal देखें')}
                  </Link>
                  <button onClick={() => scrollToSection('features-overview')} className="px-8 py-4 bg-white/10 backdrop-blur-md border border-white/30 text-white rounded-full hover:bg-white/20 transition-all font-semibold text-lg" style={{fontFamily: 'Manrope, sans-serif'}} data-testid="ae-hero-features-btn">
                    {t('See 8 Features', '8 हथियार देखें')} ↓
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* Stats Cards - Overlapping */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6 -mt-12 md:-mt-16 relative z-20 px-4 md:px-12">
            {[
              { value: '8', label: t('AI Tools', 'AI हथियार') },
              { value: '3-5x', label: t('More Admissions', 'ज़्यादा Admissions') },
              { value: '₹0', label: t('Pamphlet/Banner Cost', 'Pamphlet/Banner खर्चा') },
              { value: '24/7', label: t('AI Never Takes Leave', 'AI कभी छुट्टी नहीं लेता') },
            ].map((stat, idx) => (
              <div key={idx} className="bg-white rounded-2xl p-5 md:p-6 shadow-xl border border-slate-100 transform hover:scale-105 hover:-translate-y-1 transition-all duration-300 group" data-testid={`ae-stat-${idx}`}>
                <div className="text-2xl md:text-4xl font-black text-slate-900 group-hover:text-orange-500 transition-colors" style={{fontFamily: 'Outfit, sans-serif'}}>{stat.value}</div>
                <div className="text-slate-500 text-xs md:text-sm mt-1 font-medium" style={{fontFamily: 'Manrope, sans-serif'}}>{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Admission Problems - Bento Grid on Light Background */}
      <section className="py-24 px-4 md:px-8 relative z-10">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <span className="inline-block px-4 py-2 bg-red-100 text-red-600 rounded-full text-sm font-semibold mb-4 uppercase tracking-wider" style={{fontFamily: 'Manrope, sans-serif'}}>
              {t('Daily Problems', 'रोज़ की समस्याएं')}
            </span>
            <h2 className="text-3xl md:text-5xl font-black text-slate-900 tracking-tight mb-4" style={{fontFamily: 'Outfit, sans-serif'}}>
              {t('Principal Sahab, Do You Face These Too?', 'Principal साहब, क्या ये Problems आपकी भी हैं?')}
            </h2>
            <p className="text-slate-500 text-lg max-w-2xl mx-auto" style={{fontFamily: 'Manrope, sans-serif'}}>
              {t('Every school faces these admission challenges...', 'हर admission season में ये तकलीफें... 👇')}
            </p>
          </div>

          {/* Bento Grid */}
          <div className="grid grid-cols-1 md:grid-cols-12 gap-5">
            {problems.map((item, idx) => {
              const gridClasses = ['md:col-span-8', 'md:col-span-4', 'md:col-span-4', 'md:col-span-4', 'md:col-span-4', 'md:col-span-6 md:col-start-4'];
              const isLarge = idx === 0 || idx === 5;
              return (
                <div key={idx} className={`${gridClasses[idx] || 'md:col-span-4'} bg-white rounded-3xl p-8 border border-slate-200 shadow-sm hover:shadow-xl hover:border-red-200 transition-all duration-300 transform hover:-translate-y-1 group`} data-testid={`ae-problem-card-${idx}`}>
                  <div className={`${isLarge ? 'flex items-start gap-6' : ''}`}>
                    <div className={`${isLarge ? 'w-20 h-20' : 'w-14 h-14'} bg-red-100 rounded-2xl flex items-center justify-center text-3xl ${isLarge ? 'text-4xl' : ''} mb-5 ${isLarge ? 'mb-0 flex-shrink-0' : ''} group-hover:scale-110 transition-transform`}>
                      {item.icon}
                    </div>
                    <div>
                      <h3 className={`${isLarge ? 'text-2xl' : 'text-xl'} font-bold text-slate-900 mb-3`} style={{fontFamily: 'Outfit, sans-serif'}}>{item.title}</h3>
                      <p className="text-slate-500 leading-relaxed" style={{fontFamily: 'Manrope, sans-serif'}}>{item.desc}</p>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Before vs After - Floating Dark Island */}
      <section className="py-8 px-4 md:px-8 relative z-10">
        <div className="max-w-[95%] xl:max-w-7xl mx-auto">
          <div className="bg-slate-950 rounded-[2.5rem] md:rounded-[3rem] p-8 md:p-12 lg:p-16 shadow-2xl shadow-slate-900/20">
            <div className="text-center mb-12">
              <span className="inline-block px-4 py-2 bg-white/10 text-white/80 rounded-full text-sm font-semibold mb-4 uppercase tracking-wider border border-white/20">
                {t('See the Difference', 'फ़र्क देखिए')}
              </span>
              <h2 className="text-3xl md:text-5xl font-black text-white mb-4 tracking-tight" style={{fontFamily: 'Outfit, sans-serif'}}>
                {t('Your School — Before & After AI', 'आपका School — AI से पहले और बाद में')}
              </h2>
              <p className="text-lg text-white/60" style={{fontFamily: 'Manrope, sans-serif'}}>
                {t('See how everything changes with Admission Engine', 'Admission Engine से सब कैसे बदलता है देखिए')}
              </p>
            </div>

            {/* Before vs After Visual */}
            <div className="relative">
              <div className="hidden md:flex absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 z-30">
                <div className="bg-orange-500 text-white font-black text-xl w-16 h-16 rounded-full flex items-center justify-center shadow-2xl border-4 border-white">VS</div>
              </div>

              <div className="grid md:grid-cols-2 gap-6 md:gap-10">
                {/* BEFORE */}
                <div className="relative">
                  <div className="absolute -top-3 left-4 z-20">
                    <span className="px-5 py-2 bg-red-500 text-white font-bold rounded-full text-sm shadow-lg">{t('BEFORE — Your School Now', 'पहले — अभी आपका School')}</span>
                  </div>
                  <div className="relative rounded-2xl overflow-hidden bg-white/5 border border-white/10">
                    <img src={BEFORE_IMG} alt="Before" className="w-full h-[350px] object-cover" />
                    <div className="absolute inset-0 bg-gradient-to-t from-slate-950/95 via-slate-950/40 to-transparent"></div>
                    <div className="absolute top-6 right-4 animate-bounce" style={{animationDelay: '0s', animationDuration: '2s'}}><span className="px-3 py-1.5 bg-red-500/90 text-white text-xs font-medium rounded-full shadow-lg">📞 Endless Calls</span></div>
                    <div className="absolute top-16 left-4 animate-bounce" style={{animationDelay: '0.3s', animationDuration: '2.2s'}}><span className="px-3 py-1.5 bg-red-500/90 text-white text-xs font-medium rounded-full shadow-lg">📝 Manual Forms</span></div>
                    <div className="absolute top-28 right-6 animate-bounce" style={{animationDelay: '0.6s', animationDuration: '2.4s'}}><span className="px-3 py-1.5 bg-red-500/90 text-white text-xs font-medium rounded-full shadow-lg">📉 Low Conversion</span></div>
                    <div className="absolute bottom-24 left-4 animate-bounce" style={{animationDelay: '0.9s', animationDuration: '2.6s'}}><span className="px-3 py-1.5 bg-red-500/90 text-white text-xs font-medium rounded-full shadow-lg">💸 Fee Delays</span></div>
                    <div className="absolute bottom-0 left-0 right-0 p-5">
                      <h3 className="text-xl font-bold text-white mb-1" style={{fontFamily: 'Outfit, sans-serif'}}>😰 {t('Manual Admissions', 'Manual Admissions')}</h3>
                      <p className="text-red-300 text-sm">{t('70% leads go cold', '70% leads ठंडे हो जाते हैं')}</p>
                    </div>
                  </div>
                </div>

                {/* Mobile VS */}
                <div className="md:hidden flex items-center justify-center -my-4 relative z-20">
                  <div className="bg-orange-500 text-white font-bold text-lg w-12 h-12 rounded-full flex items-center justify-center shadow-xl border-4 border-white">VS</div>
                </div>

                {/* AFTER */}
                <div className="relative">
                  <div className="absolute -top-3 right-4 z-20">
                    <span className="px-5 py-2 bg-green-500 text-white font-bold rounded-full text-sm shadow-lg">{t('AFTER — With Shiksha Mitra AI', 'बाद में — शिक्षा मित्र AI के साथ')}</span>
                  </div>
                  <div className="relative rounded-2xl overflow-hidden bg-white/5 border border-white/10">
                    <img src={AFTER_IMG} alt="After" className="w-full h-[350px] object-cover" />
                    <div className="absolute inset-0 bg-gradient-to-t from-slate-950/95 via-slate-950/40 to-transparent"></div>
                    <div className="absolute top-6 left-4 animate-bounce" style={{animationDelay: '0.2s', animationDuration: '2.1s'}}><span className="px-3 py-1.5 bg-green-500/90 text-white text-xs font-medium rounded-full shadow-lg">🤖 AI Voice Bot</span></div>
                    <div className="absolute top-16 right-4 animate-bounce" style={{animationDelay: '0.5s', animationDuration: '2.3s'}}><span className="px-3 py-1.5 bg-green-500/90 text-white text-xs font-medium rounded-full shadow-lg">📍 Geo-Fence Marketing</span></div>
                    <div className="absolute top-28 left-6 animate-bounce" style={{animationDelay: '0.8s', animationDuration: '2.5s'}}><span className="px-3 py-1.5 bg-green-500/90 text-white text-xs font-medium rounded-full shadow-lg">💳 Padhai EMI</span></div>
                    <div className="absolute bottom-24 right-4 animate-bounce" style={{animationDelay: '1.1s', animationDuration: '2.7s'}}><span className="px-3 py-1.5 bg-green-500/90 text-white text-xs font-medium rounded-full shadow-lg">🎬 Welcome Video</span></div>
                    <div className="absolute bottom-0 left-0 right-0 p-5">
                      <h3 className="text-xl font-bold text-white mb-1" style={{fontFamily: 'Outfit, sans-serif'}}>😊 {t('AI-Powered Admissions', 'AI-Powered Admissions')}</h3>
                      <p className="text-green-300 text-sm">{t('3-5x more admissions, fully automated', '3-5x ज़्यादा admissions, fully automated')}</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Stats Bar */}
            <div className="mt-12 grid grid-cols-2 md:grid-cols-4 gap-4">
              {[
                { icon: '⏱️', value: '10x', label: t('Faster Admission Process', 'तेज़ Admission Process') },
                { icon: '📈', value: '3-5x', label: t('More Admissions', 'ज़्यादा Admissions') },
                { icon: '💰', value: '100%', label: t('Fees On Time', 'Fees समय पर') },
                { icon: '🤖', value: '24/7', label: t('AI Never Takes Leave', 'AI छुट्टी नहीं लेता') },
              ].map((stat, idx) => (
                <div key={idx} className="bg-white/5 rounded-2xl p-5 text-center border border-white/10 hover:bg-white/10 transition-all">
                  <div className="text-2xl mb-1">{stat.icon}</div>
                  <div className="text-2xl font-black text-orange-400" style={{fontFamily: 'Outfit, sans-serif'}}>{stat.value}</div>
                  <div className="text-sm text-white/60">{stat.label}</div>
                </div>
              ))}
            </div>

            <div className="text-center mt-10">
              <button onClick={handleActivateOrDemo} className="px-10 py-4 bg-green-500 text-white rounded-full hover:bg-green-600 transition-all font-bold text-lg shadow-lg shadow-green-500/30 transform hover:scale-105 inline-block" data-testid="ae-before-after-cta">
                {ctaLabel('Start Your Engine!', '🚀 अभी शुरू करें!')} →
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* 8 Features Overview - School Benefit Focused - Light Background */}
      <section id="features-overview" className="py-24 px-4 md:px-8 relative z-10">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <span className="inline-block px-4 py-2 bg-orange-100 text-orange-600 rounded-full text-sm font-semibold mb-4 uppercase tracking-wider" style={{fontFamily: 'Manrope, sans-serif'}}>
              {t('8 AI Weapons', '8 AI हथियार')}
            </span>
            <h2 className="text-3xl md:text-5xl font-black text-slate-900 tracking-tight mb-4" style={{fontFamily: 'Outfit, sans-serif'}}>
              {t('What Will Your School Get?', 'आपके School को क्या-क्या मिलेगा?')}
            </h2>
            <p className="text-slate-500 text-lg max-w-2xl mx-auto" style={{fontFamily: 'Manrope, sans-serif'}}>
              {t('Each tool designed to fill more seats with less effort', 'हर tool बना है कम मेहनत में ज़्यादा seats भरने के लिए')}
            </p>
          </div>

          {/* Bento Grid */}
          <div className="grid grid-cols-1 md:grid-cols-12 gap-5">
            {ENGINE_FEATURES.map((f, idx) => {
              const gridClasses = ['md:col-span-6', 'md:col-span-6', 'md:col-span-4', 'md:col-span-4', 'md:col-span-4', 'md:col-span-4', 'md:col-span-4', 'md:col-span-4'];
              const isWide = idx < 2;
              return (
                <div key={f.num} className={`${gridClasses[idx]} bg-white rounded-3xl border border-slate-200 shadow-sm hover:shadow-xl hover:border-orange-200 transition-all duration-300 transform hover:-translate-y-1 group cursor-pointer overflow-hidden`}
                  onClick={() => scrollToSection('features-detail')}
                  data-testid={`ae-feature-card-${f.num}`}
                >
                  <div className="p-6 md:p-8">
                    <div className={`${isWide ? 'flex items-start gap-5' : ''}`}>
                      <div className={`${isWide ? 'w-16 h-16' : 'w-14 h-14'} bg-gradient-to-br ${f.color} rounded-2xl flex items-center justify-center text-2xl ${isWide ? 'text-3xl' : ''} mb-4 ${isWide ? 'mb-0 flex-shrink-0' : ''} group-hover:scale-110 transition-transform text-white shadow-lg`}>
                        {f.icon}
                      </div>
                      <div>
                        <h3 className={`${isWide ? 'text-xl' : 'text-lg'} font-bold text-slate-900 mb-1`} style={{fontFamily: 'Outfit, sans-serif'}}>{f.title[lang]}</h3>
                        <p className="text-xs text-slate-400 mb-2 font-medium" style={{fontFamily: 'Manrope, sans-serif'}}>{f.subtitle[lang]}</p>
                        <p className="text-slate-600 text-sm leading-relaxed" style={{fontFamily: 'Manrope, sans-serif'}}>{f.benefit[lang]}</p>
                        <div className="mt-3 flex items-center text-orange-500 font-medium group-hover:text-orange-600">
                          <span className="text-sm" style={{fontFamily: 'Manrope, sans-serif'}}>{t('Learn More', 'विस्तार से जानें')}</span>
                          <svg className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" /></svg>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          <div className="text-center mt-16">
            <p className="text-slate-400 mb-6" style={{fontFamily: 'Manrope, sans-serif'}}>
              {t('Want to see what school gets and what parent gets for each feature?', 'हर feature में school को क्या मिलेगा और parent को क्या — detail में देखें?')}
            </p>
            <button onClick={() => scrollToSection('features-detail')} className="inline-flex items-center gap-2 px-8 py-3.5 bg-slate-900 hover:bg-slate-800 text-white font-semibold rounded-full shadow-lg transition-all hover:-translate-y-1" data-testid="ae-view-details-btn">
              <span>{t('See Full Details', 'पूरी Detail देखें')}</span>
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" /></svg>
            </button>
          </div>
        </div>
      </section>

      {/* LIVE DEMO - Interactive Section - Dark Island */}
      <section className="py-8 px-4 md:px-8 relative z-10" id="live-demo">
        <div className="max-w-[95%] xl:max-w-7xl mx-auto">
          <div className="bg-slate-950 rounded-[2.5rem] md:rounded-[3rem] p-8 md:p-12 lg:p-16 shadow-2xl shadow-slate-900/20">
            <div className="text-center mb-12">
              <span className="inline-block px-4 py-2 bg-green-500/20 text-green-400 rounded-full text-sm font-semibold mb-4 uppercase tracking-wider border border-green-500/30" style={{fontFamily: 'Manrope, sans-serif'}}>
                {t('Try It Now — No Login Needed', 'अभी Try करें — Login ज़रूरी नहीं')}
              </span>
              <h2 className="text-3xl md:text-5xl font-black text-white mb-4 tracking-tight" style={{fontFamily: 'Outfit, sans-serif'}}>
                {t('See It Working — Live Demo', 'देखिए कैसे काम करता है — Live Demo')}
              </h2>
              <p className="text-lg text-white/60" style={{fontFamily: 'Manrope, sans-serif'}}>
                {t('This is how parents will find your school and see available seats', 'ऐसे parents आपका school ढूंढेंगे और seats देखेंगे')}
              </p>
            </div>

            <div className="grid lg:grid-cols-2 gap-8">
              {/* School Match Swipe Demo */}
              <div data-testid="live-demo-school-match">
                <div className="flex items-center gap-2 mb-4">
                  <span className="text-2xl">💘</span>
                  <h3 className="text-xl font-bold text-white" style={{fontFamily: 'Outfit, sans-serif'}}>School Match</h3>
                  <span className="text-xs bg-pink-500/20 text-pink-400 px-2 py-0.5 rounded-full border border-pink-500/30" style={{fontFamily: 'Manrope, sans-serif'}}>Parents ऐसे school ढूंढेंगे</span>
                </div>

                {/* Card Stack */}
                <div className="relative h-[380px] mb-4">
                  {/* Background cards for stack effect */}
                  {currentSchool < DEMO_SCHOOLS.length - 1 && (
                    <div className="absolute inset-x-4 top-3 bottom-0 bg-white/5 rounded-2xl border border-white/10"></div>
                  )}

                  {currentSchool < DEMO_SCHOOLS.length ? (
                    <div className={`absolute inset-0 bg-white/10 backdrop-blur-sm rounded-2xl border border-white/20 overflow-hidden transition-all duration-300 ${
                      swipeAnim === 'left' ? '-translate-x-[120%] -rotate-12 opacity-0' :
                      swipeAnim === 'right' ? 'translate-x-[120%] rotate-12 opacity-0' : ''
                    }`} data-testid="demo-school-card">
                      {/* School Image */}
                      <div className="relative h-44">
                        <img src={DEMO_SCHOOLS[currentSchool].image} alt={DEMO_SCHOOLS[currentSchool].name} className="w-full h-full object-cover" />
                        <div className="absolute inset-0 bg-gradient-to-t from-slate-950/80 to-transparent"></div>
                        <div className="absolute top-3 right-3 bg-green-500 text-white text-xs font-bold px-2.5 py-1 rounded-full shadow-lg" style={{fontFamily: 'Manrope, sans-serif'}}>
                          {DEMO_SCHOOLS[currentSchool].match}% Match
                        </div>
                        <div className="absolute bottom-3 left-3">
                          <h4 className="text-white font-bold text-lg" style={{fontFamily: 'Outfit, sans-serif'}}>{DEMO_SCHOOLS[currentSchool].name}</h4>
                          <p className="text-white/70 text-sm" style={{fontFamily: 'Manrope, sans-serif'}}>{DEMO_SCHOOLS[currentSchool].city}</p>
                        </div>
                      </div>

                      {/* School Details */}
                      <div className="p-4 space-y-3">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-1">
                            <span className="text-yellow-400 text-sm">★</span>
                            <span className="text-white font-bold text-sm" style={{fontFamily: 'Manrope, sans-serif'}}>{DEMO_SCHOOLS[currentSchool].rating}</span>
                          </div>
                          <span className="text-orange-400 font-bold" style={{fontFamily: 'Outfit, sans-serif'}}>{DEMO_SCHOOLS[currentSchool].fees}</span>
                          <span className="text-white/50 text-xs" style={{fontFamily: 'Manrope, sans-serif'}}>{DEMO_SCHOOLS[currentSchool].students} students</span>
                        </div>

                        {/* Facilities */}
                        <div className="flex flex-wrap gap-1.5">
                          {DEMO_SCHOOLS[currentSchool].facilities.map((f, i) => (
                            <span key={i} className="text-xs bg-white/10 text-white/70 px-2 py-1 rounded-full border border-white/10" style={{fontFamily: 'Manrope, sans-serif'}}>{f}</span>
                          ))}
                        </div>

                        {/* Seat Info Preview */}
                        <div className="bg-white/5 rounded-xl p-2.5 border border-white/10">
                          <div className="flex items-center justify-between text-xs mb-1.5">
                            <span className="text-white/50" style={{fontFamily: 'Manrope, sans-serif'}}>{t('Class 1 Seats', 'Class 1 Seats')}</span>
                            <span className="text-orange-400 font-bold" style={{fontFamily: 'Manrope, sans-serif'}}>{DEMO_SCHOOLS[currentSchool].seatsFilled}/{DEMO_SCHOOLS[currentSchool].seatsTotal}</span>
                          </div>
                          <div className="w-full bg-white/10 rounded-full h-2">
                            <div className="h-2 rounded-full bg-gradient-to-r from-orange-500 to-red-500 transition-all duration-1000" style={{width: `${(DEMO_SCHOOLS[currentSchool].seatsFilled / DEMO_SCHOOLS[currentSchool].seatsTotal) * 100}%`}}></div>
                          </div>
                          {DEMO_SCHOOLS[currentSchool].seatsFilled / DEMO_SCHOOLS[currentSchool].seatsTotal > 0.8 && (
                            <p className="text-red-400 text-[10px] mt-1 font-medium animate-pulse" style={{fontFamily: 'Manrope, sans-serif'}}>
                              🔥 {t('Only', 'सिर्फ')} {DEMO_SCHOOLS[currentSchool].seatsTotal - DEMO_SCHOOLS[currentSchool].seatsFilled} {t('seats left!', 'seats बची!')}
                            </p>
                          )}
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div className="absolute inset-0 flex flex-col items-center justify-center bg-white/5 rounded-2xl border border-white/20" data-testid="demo-all-swiped">
                      <span className="text-5xl mb-3">🎉</span>
                      <p className="text-white font-bold text-lg mb-1" style={{fontFamily: 'Outfit, sans-serif'}}>{t('You liked', 'आपने like किए')} {likedSchools.length} {t('schools!', 'schools!')}</p>
                      <p className="text-white/50 text-sm mb-4" style={{fontFamily: 'Manrope, sans-serif'}}>{t('Sign up to see 1000+ schools', 'Sign up करें 1000+ schools देखने के लिए')}</p>
                      <button onClick={() => { setCurrentSchool(0); setLikedSchools([]); }} className="text-sm text-orange-400 hover:text-orange-300 underline" style={{fontFamily: 'Manrope, sans-serif'}}>{t('Try Again', 'फिर से Try करें')}</button>
                    </div>
                  )}

                  {/* Match Animation Overlay */}
                  {showMatch && (
                    <div className="absolute inset-0 flex items-center justify-center bg-green-500/20 backdrop-blur-sm rounded-2xl z-10 animate-pulse">
                      <div className="text-center">
                        <span className="text-6xl block mb-2">💘</span>
                        <p className="text-green-400 font-black text-3xl" style={{fontFamily: 'Outfit, sans-serif'}}>It's a Match!</p>
                      </div>
                    </div>
                  )}
                </div>

                {/* Swipe Buttons */}
                {currentSchool < DEMO_SCHOOLS.length && (
                  <div className="flex items-center justify-center gap-6">
                    <button
                      onClick={() => {
                        setSwipeAnim('left');
                        setTimeout(() => { setSwipeAnim(null); setCurrentSchool(prev => prev + 1); }, 300);
                      }}
                      className="w-14 h-14 bg-red-500/20 border-2 border-red-500/40 rounded-full flex items-center justify-center text-red-400 hover:bg-red-500/30 hover:scale-110 transition-all"
                      data-testid="demo-skip-btn"
                    >
                      <svg className="w-7 h-7" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M6 18L18 6M6 6l12 12" /></svg>
                    </button>
                    <span className="text-white/30 text-sm" style={{fontFamily: 'Manrope, sans-serif'}}>{currentSchool + 1}/{DEMO_SCHOOLS.length}</span>
                    <button
                      onClick={() => {
                        setSwipeAnim('right');
                        setShowMatch(true);
                        setLikedSchools(prev => [...prev, DEMO_SCHOOLS[currentSchool].name]);
                        setTimeout(() => { setShowMatch(false); setSwipeAnim(null); setCurrentSchool(prev => prev + 1); }, 800);
                      }}
                      className="w-14 h-14 bg-green-500/20 border-2 border-green-500/40 rounded-full flex items-center justify-center text-green-400 hover:bg-green-500/30 hover:scale-110 transition-all"
                      data-testid="demo-like-btn"
                    >
                      <svg className="w-7 h-7" fill="currentColor" viewBox="0 0 24 24"><path d="M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z"/></svg>
                    </button>
                  </div>
                )}
              </div>

              {/* Live Seat Counter Demo */}
              <div data-testid="live-demo-seat-counter">
                <div className="flex items-center gap-2 mb-4">
                  <span className="text-2xl">🔥</span>
                  <h3 className="text-xl font-bold text-white" style={{fontFamily: 'Outfit, sans-serif'}}>Live Seat Counter</h3>
                  <span className="text-xs bg-red-500/20 text-red-400 px-2 py-0.5 rounded-full border border-red-500/30" style={{fontFamily: 'Manrope, sans-serif'}}>Parents को ऐसा दिखेगा</span>
                </div>

                <div className="bg-white/10 backdrop-blur-sm rounded-2xl border border-white/20 overflow-hidden">
                  {/* School Header */}
                  <div className="bg-gradient-to-r from-orange-500/20 to-red-500/20 p-4 border-b border-white/10">
                    <div className="flex items-center justify-between">
                      <div>
                        <h4 className="text-white font-bold" style={{fontFamily: 'Outfit, sans-serif'}}>Delhi Public Academy</h4>
                        <p className="text-white/50 text-xs" style={{fontFamily: 'Manrope, sans-serif'}}>Lucknow • Admission 2025-26</p>
                      </div>
                      <div className="flex items-center gap-1.5 bg-red-500/30 px-3 py-1.5 rounded-full border border-red-500/40 animate-pulse">
                        <span className="w-2 h-2 bg-red-400 rounded-full"></span>
                        <span className="text-red-300 text-xs font-bold" style={{fontFamily: 'Manrope, sans-serif'}}>LIVE</span>
                      </div>
                    </div>
                  </div>

                  {/* Seat Counters by Class */}
                  <div className="p-4 space-y-3">
                    {[
                      { cls: 'Nursery', total: 40, filled: 38, viewers: 12 },
                      { cls: 'LKG', total: 40, filled: 35, viewers: 8 },
                      { cls: 'UKG', total: 35, filled: 30, viewers: 5 },
                      { cls: 'Class 1', total: 45, filled: 34, viewers: 15 },
                      { cls: 'Class 2', total: 40, filled: 28, viewers: 3 },
                    ].map((seat, idx) => {
                      const pct = (seat.filled / seat.total) * 100;
                      const isHot = pct >= 85;
                      const isWarm = pct >= 70 && pct < 85;
                      return (
                        <div key={idx} className="bg-white/5 rounded-xl p-3 border border-white/10 hover:bg-white/[0.08] transition-all" data-testid={`seat-row-${idx}`}>
                          <div className="flex items-center justify-between mb-2">
                            <span className="text-white font-medium text-sm" style={{fontFamily: 'Manrope, sans-serif'}}>{seat.cls}</span>
                            <div className="flex items-center gap-3">
                              <span className="flex items-center gap-1 text-[11px] text-white/40" style={{fontFamily: 'Manrope, sans-serif'}}>
                                <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 24 24"><path d="M12 4.5C7 4.5 2.73 7.61 1 12c1.73 4.39 6 7.5 11 7.5s9.27-3.11 11-7.5c-1.73-4.39-6-7.5-11-7.5zM12 17c-2.76 0-5-2.24-5-5s2.24-5 5-5 5 2.24 5 5-2.24 5-5 5zm0-8c-1.66 0-3 1.34-3 3s1.34 3 3 3 3-1.34 3-3-1.34-3-3-3z"/></svg>
                                {seat.viewers} {t('viewing', 'देख रहे')}
                              </span>
                              <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${
                                isHot ? 'bg-red-500/30 text-red-300 animate-pulse' :
                                isWarm ? 'bg-orange-500/30 text-orange-300' :
                                'bg-green-500/30 text-green-300'
                              }`} style={{fontFamily: 'Manrope, sans-serif'}}>
                                {seat.filled}/{seat.total}
                              </span>
                            </div>
                          </div>
                          <div className="w-full bg-white/10 rounded-full h-2.5">
                            <div className={`h-2.5 rounded-full transition-all duration-1000 ${
                              isHot ? 'bg-gradient-to-r from-red-500 to-red-400' :
                              isWarm ? 'bg-gradient-to-r from-orange-500 to-amber-400' :
                              'bg-gradient-to-r from-green-500 to-emerald-400'
                            }`} style={{width: `${pct}%`}}></div>
                          </div>
                          {isHot && (
                            <div className="flex items-center justify-between mt-1.5">
                              <p className="text-red-400 text-[11px] font-medium animate-pulse" style={{fontFamily: 'Manrope, sans-serif'}}>
                                🔥 {t('Only', 'सिर्फ')} {seat.total - seat.filled} {t('seats left!', 'seats बची!')}
                              </p>
                              <span className="text-[10px] text-white/30 bg-white/5 px-1.5 py-0.5 rounded" style={{fontFamily: 'Manrope, sans-serif'}}>Waitlist ON</span>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>

                  {/* Bottom CTA */}
                  <div className="p-4 border-t border-white/10 bg-white/5">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-white/40 text-xs" style={{fontFamily: 'Manrope, sans-serif'}}>{t('Total seats across all classes', 'सभी classes में कुल seats')}</p>
                        <p className="text-white font-bold text-lg" style={{fontFamily: 'Outfit, sans-serif'}}>165/200 <span className="text-orange-400 text-sm font-medium">(82.5% {t('filled', 'भरे')})</span></p>
                      </div>
                      <button onClick={handleActivateOrDemo} className="px-4 py-2 bg-orange-500 text-white rounded-full text-sm font-bold hover:bg-orange-600 transition shadow-lg" style={{fontFamily: 'Manrope, sans-serif'}} data-testid="seat-counter-cta">
                        {t('Apply Now', 'अभी Apply करें')}
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* AI Assessment Games Preview */}
            <div className="mt-10 pt-8 border-t border-white/10" data-testid="live-demo-assessment">
              <div className="flex items-center gap-2 mb-6">
                <span className="text-2xl">🎮</span>
                <h3 className="text-xl font-bold text-white" style={{fontFamily: 'Outfit, sans-serif'}}>AI Child Assessment</h3>
                <span className="text-xs bg-purple-500/20 text-purple-400 px-2 py-0.5 rounded-full border border-purple-500/30" style={{fontFamily: 'Manrope, sans-serif'}}>बच्चे ऐसे game खेलेंगे</span>
              </div>

              {!activeGame ? (
                /* Game Selection — with optional name input */
                <div data-testid="game-selection">
                  {/* Child Name Input */}
                  <div className="mb-6 max-w-sm mx-auto" data-testid="child-name-input-section">
                    <label className="text-white/60 text-sm block mb-2 text-center" style={{fontFamily: 'Manrope, sans-serif'}}>
                      {t("Enter child's name for personalized result card", 'बच्चे का नाम लिखें — personalized result card के लिए')}
                    </label>
                    <input
                      type="text"
                      value={childName}
                      onChange={(e) => setChildName(e.target.value)}
                      placeholder={t("e.g. Aarav, Priya...", "जैसे Aarav, Priya...")}
                      className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-3 text-white text-center placeholder-white/30 focus:outline-none focus:border-purple-500/50 focus:bg-white/15 transition"
                      style={{fontFamily: 'Manrope, sans-serif'}}
                      data-testid="child-name-input"
                    />
                  </div>

                  <div className="grid sm:grid-cols-2 gap-4">
                  <button
                    onClick={() => { resetGames(); setActiveGame('pattern'); }}
                    className="bg-white/5 rounded-2xl p-6 border border-white/10 hover:bg-white/10 hover:border-purple-500/30 transition-all text-left group"
                    data-testid="select-pattern-game"
                  >
                    <div className="text-4xl mb-3 group-hover:scale-110 transition-transform">🧩</div>
                    <h4 className="text-white font-bold text-lg mb-1" style={{fontFamily: 'Outfit, sans-serif'}}>{t('Pattern Recognition', 'Pattern पहचानो')}</h4>
                    <p className="text-white/50 text-sm" style={{fontFamily: 'Manrope, sans-serif'}}>{t('What comes next in the sequence?', 'Sequence में आगे क्या आएगा?')}</p>
                    <div className="mt-3 flex items-center text-purple-400 font-medium text-sm group-hover:text-purple-300">
                      <span style={{fontFamily: 'Manrope, sans-serif'}}>{t('Play Now', 'खेलें')}</span>
                      <svg className="w-4 h-4 ml-1 group-hover:translate-x-1 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" /></svg>
                    </div>
                  </button>
                  <button
                    onClick={() => { resetGames(); setActiveGame('memory'); initMemoryGame(); }}
                    className="bg-white/5 rounded-2xl p-6 border border-white/10 hover:bg-white/10 hover:border-purple-500/30 transition-all text-left group"
                    data-testid="select-memory-game"
                  >
                    <div className="text-4xl mb-3 group-hover:scale-110 transition-transform">🃏</div>
                    <h4 className="text-white font-bold text-lg mb-1" style={{fontFamily: 'Outfit, sans-serif'}}>{t('Memory Match', 'Memory Match')}</h4>
                    <p className="text-white/50 text-sm" style={{fontFamily: 'Manrope, sans-serif'}}>{t('Find matching pairs — tests memory & focus', 'Matching pairs खोजें — memory & focus test')}</p>
                    <div className="mt-3 flex items-center text-purple-400 font-medium text-sm group-hover:text-purple-300">
                      <span style={{fontFamily: 'Manrope, sans-serif'}}>{t('Play Now', 'खेलें')}</span>
                      <svg className="w-4 h-4 ml-1 group-hover:translate-x-1 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" /></svg>
                    </div>
                  </button>
                </div>
                </div>
              ) : activeGame === 'pattern' ? (
                /* Pattern Recognition Game */
                <div className="bg-white/5 rounded-2xl p-6 border border-white/10" data-testid="pattern-game">
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center gap-2">
                      <span className="text-xl">🧩</span>
                      <span className="text-white font-bold" style={{fontFamily: 'Outfit, sans-serif'}}>{t('Pattern Recognition', 'Pattern पहचानो')}</span>
                    </div>
                    <div className="flex items-center gap-3">
                      <span className="text-xs bg-purple-500/20 text-purple-400 px-2 py-1 rounded-full" style={{fontFamily: 'Manrope, sans-serif'}} data-testid="pattern-progress">
                        {patternQ + 1}/{patternQuestions.length}
                      </span>
                      <span className="text-xs bg-green-500/20 text-green-400 px-2 py-1 rounded-full" style={{fontFamily: 'Manrope, sans-serif'}} data-testid="pattern-score">
                        Score: {patternScore}
                      </span>
                      <button onClick={resetGames} className="text-white/40 hover:text-white text-xs" data-testid="pattern-back-btn">✕</button>
                    </div>
                  </div>

                  {!gameComplete ? (
                    <>
                      <p className="text-white/60 text-sm mb-3" style={{fontFamily: 'Manrope, sans-serif'}}>{t('What comes next?', 'आगे क्या आएगा?')}</p>

                      {/* Pattern Sequence */}
                      <div className="flex items-center justify-center gap-3 mb-2 py-4 bg-white/5 rounded-xl" data-testid="pattern-sequence">
                        {patternQuestions[patternQ].seq.map((item, i) => (
                          <span key={i} className="text-3xl md:text-4xl">{item}</span>
                        ))}
                        <span className="text-3xl md:text-4xl text-white/30 animate-pulse">?</span>
                      </div>

                      <p className="text-white/30 text-xs text-center mb-4" style={{fontFamily: 'Manrope, sans-serif'}}>{patternQuestions[patternQ].hint[lang]}</p>

                      {/* Options */}
                      <div className="grid grid-cols-4 gap-3" data-testid="pattern-options">
                        {patternQuestions[patternQ].options.map((opt, i) => (
                          <button
                            key={i}
                            onClick={() => !patternFeedback && handlePatternAnswer(opt)}
                            className={`py-4 rounded-xl text-3xl transition-all ${
                              patternFeedback && opt === patternQuestions[patternQ].answer
                                ? 'bg-green-500/30 border-2 border-green-500 scale-110'
                                : patternFeedback === 'wrong' && opt !== patternQuestions[patternQ].answer
                                  ? 'bg-white/5 border border-white/10 opacity-50'
                                  : 'bg-white/5 border border-white/10 hover:bg-white/10 hover:border-purple-500/30 hover:scale-105'
                            }`}
                            data-testid={`pattern-option-${i}`}
                          >
                            {opt}
                          </button>
                        ))}
                      </div>

                      {/* Feedback */}
                      {patternFeedback && (
                        <div className={`text-center mt-3 py-2 rounded-xl text-sm font-bold ${patternFeedback === 'correct' ? 'bg-green-500/20 text-green-400' : 'bg-red-500/20 text-red-400'}`} data-testid="pattern-feedback">
                          {patternFeedback === 'correct' ? (t('Correct!', 'सही जवाब! 🎉')) : (t('Try next one!', 'अगला try करें!'))}
                        </div>
                      )}
                    </>
                  ) : (
                    /* Pattern Game Complete - Share Result Card */
                    <div data-testid="pattern-complete">
                      {/* Downloadable Result Card */}
                      <div ref={activeGame === 'pattern' ? resultCardRef : null} className="rounded-2xl overflow-hidden mx-auto max-w-md" style={{background: 'linear-gradient(135deg, #1e1b4b 0%, #0f172a 50%, #1e1b4b 100%)'}}>
                        <div className="p-6 text-center">
                          {/* Branding */}
                          <div className="flex items-center justify-center gap-2 mb-4">
                            <img src={LOGO_URL} alt="Logo" className="h-8 w-8 rounded-lg" />
                            <span className="text-lg font-bold text-white" style={{fontFamily: 'Outfit, sans-serif'}}>शिक्षा मित्र</span>
                          </div>

                          <span className="text-4xl block mb-2">🏆</span>
                          <h4 className="text-white font-black text-xl mb-1" style={{fontFamily: 'Outfit, sans-serif'}}>Pattern Recognition</h4>
                          {childName && <p className="text-yellow-300 font-bold text-sm mb-1" style={{fontFamily: 'Outfit, sans-serif'}} data-testid="pattern-child-name">{childName}</p>}
                          <p className="text-purple-300 text-xs mb-4" style={{fontFamily: 'Manrope, sans-serif'}}>AI Child Assessment</p>

                          {/* Score Circle */}
                          <div className="w-24 h-24 mx-auto rounded-full flex items-center justify-center mb-4" style={{background: 'linear-gradient(135deg, #a855f7, #6366f1)'}}>
                            <div className="text-center">
                              <span className="text-white font-black text-3xl block" style={{fontFamily: 'Outfit, sans-serif'}}>{patternScore}</span>
                              <span className="text-white/70 text-[10px]">/ {patternQuestions.length}</span>
                            </div>
                          </div>

                          {/* AI Analysis */}
                          <div className="bg-white/10 rounded-xl p-3 mb-4 border border-white/10">
                            <p className="text-purple-300 text-xs font-bold mb-1" style={{fontFamily: 'Manrope, sans-serif'}}>AI Analysis</p>
                            <p className="text-white/80 text-xs" style={{fontFamily: 'Manrope, sans-serif'}}>
                              {patternScore >= 3 ? t('Excellent pattern recognition! Strong logical thinking.', 'बेहतरीन pattern recognition! Strong logical thinking।') :
                               patternScore >= 2 ? t('Good pattern skills! Developing logical thinking.', 'अच्छी pattern skills! Logical thinking develop हो रही।') :
                               t('Creative thinker! Needs pattern practice.', 'Creative thinker! Pattern practice चाहिए।')}
                            </p>
                          </div>

                          {/* Skills */}
                          <div className="flex justify-center gap-2 mb-3 flex-wrap">
                            {[
                              { label: t('Logic','Logic'), pct: patternScore >= 3 ? 95 : patternScore >= 2 ? 70 : 45 },
                              { label: t('Pattern','Pattern'), pct: patternScore >= 3 ? 90 : patternScore >= 2 ? 65 : 40 },
                              { label: t('Focus','Focus'), pct: patternScore >= 3 ? 85 : patternScore >= 2 ? 75 : 55 },
                            ].map((s,i) => (
                              <div key={i} className="bg-white/5 rounded-lg px-3 py-1.5 border border-white/10">
                                <p className="text-white/40 text-[9px]" style={{fontFamily: 'Manrope, sans-serif'}}>{s.label}</p>
                                <p className="text-white font-bold text-sm" style={{fontFamily: 'Outfit, sans-serif'}}>{s.pct}%</p>
                              </div>
                            ))}
                          </div>

                          <p className="text-white/30 text-[10px]" style={{fontFamily: 'Manrope, sans-serif'}}>shiksha-mitra.com • AI-Powered Assessment</p>
                        </div>
                      </div>

                      {/* Share Actions */}
                      <div className="flex flex-wrap gap-2 justify-center mt-5" data-testid="pattern-share-actions">
                        <button onClick={downloadResultCard} disabled={shareDownloading} className="flex items-center gap-2 px-4 py-2.5 bg-purple-500 text-white rounded-full text-sm font-bold hover:bg-purple-600 transition shadow-lg disabled:opacity-50" style={{fontFamily: 'Manrope, sans-serif'}} data-testid="pattern-download-btn">
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" /></svg>
                          {shareDownloading ? t('Saving...','Save हो रहा...') : t('Download Card','Card Download करें')}
                        </button>
                        <button onClick={() => shareResultWhatsApp('pattern')} className="flex items-center gap-2 px-4 py-2.5 bg-green-500 text-white rounded-full text-sm font-bold hover:bg-green-600 transition shadow-lg" style={{fontFamily: 'Manrope, sans-serif'}} data-testid="pattern-share-whatsapp">
                          <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>
                          WhatsApp
                        </button>
                        <button onClick={() => copyResultText('pattern')} className="flex items-center gap-2 px-4 py-2.5 bg-white/10 text-white rounded-full text-sm font-medium hover:bg-white/20 transition border border-white/20" style={{fontFamily: 'Manrope, sans-serif'}} data-testid="pattern-copy-btn">
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 5H6a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2v-1M8 5a2 2 0 002 2h2a2 2 0 002-2M8 5a2 2 0 012-2h2a2 2 0 012 2m0 0h2a2 2 0 012 2v3m2 4H10m0 0l3-3m-3 3l3 3" /></svg>
                          {t('Copy','Copy')}
                        </button>
                      </div>
                      <div className="flex gap-3 justify-center mt-3">
                        <button onClick={resetGames} className="px-4 py-2 text-sm text-white/50 hover:text-white underline" style={{fontFamily: 'Manrope, sans-serif'}} data-testid="pattern-back-to-games">{t('Back to Games', 'Games पर वापस')}</button>
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                /* Memory Match Game */
                <div className="bg-white/5 rounded-2xl p-6 border border-white/10" data-testid="memory-game">
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center gap-2">
                      <span className="text-xl">🃏</span>
                      <span className="text-white font-bold" style={{fontFamily: 'Outfit, sans-serif'}}>{t('Memory Match', 'Memory Match')}</span>
                    </div>
                    <div className="flex items-center gap-3">
                      <span className="text-xs bg-blue-500/20 text-blue-400 px-2 py-1 rounded-full" style={{fontFamily: 'Manrope, sans-serif'}} data-testid="memory-pairs">
                        {Math.floor(memoryMatched.length / 2)}/6 {t('pairs', 'pairs')}
                      </span>
                      <span className="text-xs bg-orange-500/20 text-orange-400 px-2 py-1 rounded-full" style={{fontFamily: 'Manrope, sans-serif'}} data-testid="memory-moves">
                        {memoryMoves} {t('moves', 'moves')}
                      </span>
                      <button onClick={resetGames} className="text-white/40 hover:text-white text-xs" data-testid="memory-back-btn">✕</button>
                    </div>
                  </div>

                  {!gameComplete ? (
                    <>
                      <p className="text-white/60 text-sm mb-4" style={{fontFamily: 'Manrope, sans-serif'}}>{t('Find all 6 matching pairs!', 'सभी 6 matching pairs खोजें!')}</p>

                      <div className="grid grid-cols-4 sm:grid-cols-6 gap-2 max-w-lg mx-auto" data-testid="memory-grid">
                        {memoryCards.map((card, idx) => {
                          const isFlipped = memoryFlipped.includes(idx) || memoryMatched.includes(idx);
                          const isMatched = memoryMatched.includes(idx);
                          return (
                            <button
                              key={card.id}
                              onClick={() => handleMemoryClick(idx)}
                              className={`aspect-square rounded-xl text-2xl flex items-center justify-center transition-all duration-300 ${
                                isMatched ? 'bg-green-500/20 border-2 border-green-500/50 scale-95' :
                                isFlipped ? 'bg-purple-500/20 border-2 border-purple-500/50 scale-105' :
                                'bg-white/10 border border-white/20 hover:bg-white/15 hover:scale-105 cursor-pointer'
                              }`}
                              data-testid={`memory-card-${idx}`}
                            >
                              {isFlipped ? card.emoji : '❓'}
                            </button>
                          );
                        })}
                      </div>
                    </>
                  ) : (
                    /* Memory Game Complete - Share Result Card */
                    <div data-testid="memory-complete">
                      {/* Downloadable Result Card */}
                      <div ref={activeGame === 'memory' ? resultCardRef : null} className="rounded-2xl overflow-hidden mx-auto max-w-md" style={{background: 'linear-gradient(135deg, #0c4a6e 0%, #0f172a 50%, #0c4a6e 100%)'}}>
                        <div className="p-6 text-center">
                          {/* Branding */}
                          <div className="flex items-center justify-center gap-2 mb-4">
                            <img src={LOGO_URL} alt="Logo" className="h-8 w-8 rounded-lg" />
                            <span className="text-lg font-bold text-white" style={{fontFamily: 'Outfit, sans-serif'}}>शिक्षा मित्र</span>
                          </div>

                          <span className="text-4xl block mb-2">🧠</span>
                          <h4 className="text-white font-black text-xl mb-1" style={{fontFamily: 'Outfit, sans-serif'}}>Memory Match</h4>
                          {childName && <p className="text-yellow-300 font-bold text-sm mb-1" style={{fontFamily: 'Outfit, sans-serif'}} data-testid="memory-child-name">{childName}</p>}
                          <p className="text-blue-300 text-xs mb-4" style={{fontFamily: 'Manrope, sans-serif'}}>AI Child Assessment</p>

                          {/* Moves Circle */}
                          <div className="w-24 h-24 mx-auto rounded-full flex items-center justify-center mb-4" style={{background: 'linear-gradient(135deg, #3b82f6, #06b6d4)'}}>
                            <div className="text-center">
                              <span className="text-white font-black text-3xl block" style={{fontFamily: 'Outfit, sans-serif'}}>{memoryMoves}</span>
                              <span className="text-white/70 text-[10px]">moves</span>
                            </div>
                          </div>

                          {/* AI Analysis */}
                          <div className="bg-white/10 rounded-xl p-3 mb-4 border border-white/10">
                            <p className="text-blue-300 text-xs font-bold mb-1" style={{fontFamily: 'Manrope, sans-serif'}}>AI Analysis</p>
                            <p className="text-white/80 text-xs" style={{fontFamily: 'Manrope, sans-serif'}}>
                              {memoryMoves <= 8 ? t('Outstanding memory! Exceptional focus and recall.', 'शानदार memory! Exceptional focus और recall।') :
                               memoryMoves <= 12 ? t('Great memory skills! Good concentration.', 'अच्छी memory skills! Good concentration।') :
                               t('Developing memory. Keep practicing!', 'Memory develop हो रही। Practice जारी रखें!')}
                            </p>
                          </div>

                          {/* Skills */}
                          <div className="flex justify-center gap-2 mb-3 flex-wrap">
                            {[
                              { label: t('Memory','Memory'), pct: memoryMoves <= 8 ? 95 : memoryMoves <= 12 ? 75 : 50 },
                              { label: t('Focus','Focus'), pct: memoryMoves <= 8 ? 90 : memoryMoves <= 12 ? 70 : 45 },
                              { label: t('Speed','Speed'), pct: memoryMoves <= 8 ? 88 : memoryMoves <= 12 ? 65 : 40 },
                            ].map((s,i) => (
                              <div key={i} className="bg-white/5 rounded-lg px-3 py-1.5 border border-white/10">
                                <p className="text-white/40 text-[9px]" style={{fontFamily: 'Manrope, sans-serif'}}>{s.label}</p>
                                <p className="text-white font-bold text-sm" style={{fontFamily: 'Outfit, sans-serif'}}>{s.pct}%</p>
                              </div>
                            ))}
                          </div>

                          <p className="text-white/30 text-[10px]" style={{fontFamily: 'Manrope, sans-serif'}}>shiksha-mitra.com • AI-Powered Assessment</p>
                        </div>
                      </div>

                      {/* Share Actions */}
                      <div className="flex flex-wrap gap-2 justify-center mt-5" data-testid="memory-share-actions">
                        <button onClick={downloadResultCard} disabled={shareDownloading} className="flex items-center gap-2 px-4 py-2.5 bg-blue-500 text-white rounded-full text-sm font-bold hover:bg-blue-600 transition shadow-lg disabled:opacity-50" style={{fontFamily: 'Manrope, sans-serif'}} data-testid="memory-download-btn">
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" /></svg>
                          {shareDownloading ? t('Saving...','Save हो रहा...') : t('Download Card','Card Download करें')}
                        </button>
                        <button onClick={() => shareResultWhatsApp('memory')} className="flex items-center gap-2 px-4 py-2.5 bg-green-500 text-white rounded-full text-sm font-bold hover:bg-green-600 transition shadow-lg" style={{fontFamily: 'Manrope, sans-serif'}} data-testid="memory-share-whatsapp">
                          <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>
                          WhatsApp
                        </button>
                        <button onClick={() => copyResultText('memory')} className="flex items-center gap-2 px-4 py-2.5 bg-white/10 text-white rounded-full text-sm font-medium hover:bg-white/20 transition border border-white/20" style={{fontFamily: 'Manrope, sans-serif'}} data-testid="memory-copy-btn">
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 5H6a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2v-1M8 5a2 2 0 002 2h2a2 2 0 002-2M8 5a2 2 0 012-2h2a2 2 0 012 2m0 0h2a2 2 0 012 2v3m2 4H10m0 0l3-3m-3 3l3 3" /></svg>
                          {t('Copy','Copy')}
                        </button>
                      </div>
                      <div className="flex gap-3 justify-center mt-3">
                        <button onClick={() => { initMemoryGame(); setGameComplete(false); }} className="px-4 py-2 text-sm bg-blue-500/20 text-blue-400 rounded-full hover:bg-blue-500/30 transition" style={{fontFamily: 'Manrope, sans-serif'}} data-testid="memory-play-again">{t('Play Again', 'फिर खेलें')}</button>
                        <button onClick={resetGames} className="px-4 py-2 text-sm text-white/50 hover:text-white underline" style={{fontFamily: 'Manrope, sans-serif'}} data-testid="memory-back-to-games">{t('Back to Games', 'Games पर वापस')}</button>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Assessment CTA */}
              <div className="mt-6 bg-gradient-to-r from-purple-500/10 to-indigo-500/10 rounded-2xl p-5 border border-purple-500/20">
                <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
                  <div>
                    <p className="text-white font-bold" style={{fontFamily: 'Outfit, sans-serif'}}>{t('Full AI Assessment has 15+ games!', 'Full AI Assessment में 15+ games हैं!')}</p>
                    <p className="text-white/50 text-sm" style={{fontFamily: 'Manrope, sans-serif'}}>{t('Cognitive, creative, logical — complete child profile in 30 minutes', 'Cognitive, creative, logical — 30 minutes में complete child profile')}</p>
                  </div>
                  <button onClick={handleActivateOrDemo} className="px-5 py-2.5 bg-purple-500 text-white rounded-full text-sm font-bold hover:bg-purple-600 transition shadow-lg flex-shrink-0" style={{fontFamily: 'Manrope, sans-serif'}} data-testid="assessment-cta">
                    {t('Get Full Assessment', 'Full Assessment लें')} →
                  </button>
                </div>
              </div>
            </div>

            {/* Bottom CTA */}
            <div className="text-center mt-12 pt-8 border-t border-white/10">
              <p className="text-white/50 text-sm mb-4" style={{fontFamily: 'Manrope, sans-serif'}}>
                {t('This is just a preview — imagine this with YOUR school\'s real data!', 'ये सिर्फ demo है — सोचिए आपके SCHOOL के real data के साथ कैसा दिखेगा!')}
              </p>
              <button onClick={handleActivateOrDemo} className="px-10 py-4 bg-orange-500 text-white rounded-full hover:bg-orange-600 transition-all font-bold text-lg shadow-lg shadow-orange-500/30 transform hover:scale-105 inline-block" style={{fontFamily: 'Manrope, sans-serif'}} data-testid="ae-demo-cta">
                {isLoggedIn ? t('Activate Now', 'अभी Activate करें') : t('Get This For My School — Free Demo', 'मेरे School के लिए बनवाएं — Free Demo')} →
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Feature Details - Dark Island with Accordion */}
      <section id="features-detail" className="py-8 px-4 md:px-8 relative z-10">
        <div className="max-w-[95%] xl:max-w-7xl mx-auto">
          <div className="bg-slate-950 rounded-[2.5rem] md:rounded-[3rem] p-8 md:p-12 lg:p-16 shadow-2xl shadow-slate-900/20">
            <div className="text-center mb-12">
              <span className="inline-block px-4 py-2 bg-white/10 text-white/80 rounded-full text-sm font-semibold mb-4 uppercase tracking-wider border border-white/20" style={{fontFamily: 'Manrope, sans-serif'}}>
                {t('Detailed Breakdown', 'पूरी जानकारी')}
              </span>
              <h2 className="text-3xl md:text-5xl font-black text-white mb-4 tracking-tight" style={{fontFamily: 'Outfit, sans-serif'}}>
                {t('What School Gets & What Parent Gets', 'School को क्या मिलेगा और Parent को क्या')}
              </h2>
              <p className="text-lg text-white/60" style={{fontFamily: 'Manrope, sans-serif'}}>
                {t('Click any feature to see details', 'किसी भी feature पर click करें — पूरी detail देखें')}
              </p>
            </div>

            <div className="space-y-4">
              {ENGINE_FEATURES.map((f) => {
                const isExpanded = expandedFeature === f.num;
                return (
                  <div key={f.num} id={`feature-${f.num}`} className="scroll-mt-24 bg-white/5 rounded-2xl border border-white/10 overflow-hidden hover:bg-white/[0.07] transition-all" data-testid={`ae-feature-detail-${f.num}`}>
                    <div className="p-5 md:p-6 flex items-center justify-between cursor-pointer group" onClick={() => setExpandedFeature(isExpanded ? null : f.num)}>
                      <div className="flex items-center gap-4">
                        <div className={`w-12 h-12 bg-gradient-to-br ${f.color} rounded-xl flex items-center justify-center text-xl text-white shadow-lg group-hover:scale-110 transition-transform`}>{f.icon}</div>
                        <div>
                          <p className="text-xs text-white/40 font-medium" style={{fontFamily: 'Manrope, sans-serif'}}>Feature #{f.num}</p>
                          <h3 className="text-lg font-bold text-white" style={{fontFamily: 'Outfit, sans-serif'}}>{f.title[lang]}</h3>
                          <p className="text-sm text-white/60" style={{fontFamily: 'Manrope, sans-serif'}}>{f.subtitle[lang]}</p>
                        </div>
                      </div>
                      <svg className={`w-6 h-6 text-white/50 transition-transform shrink-0 ${isExpanded ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" /></svg>
                    </div>

                    {isExpanded && (
                      <div className="px-5 md:px-6 pb-6">
                        <div className="grid sm:grid-cols-2 gap-4">
                          <div className="bg-blue-500/10 rounded-2xl p-5 border border-blue-500/20">
                            <h4 className="font-bold text-blue-400 text-sm mb-3 flex items-center gap-2" style={{fontFamily: 'Outfit, sans-serif'}}>
                              <span className="w-6 h-6 bg-blue-500/20 rounded-full flex items-center justify-center text-xs">🏫</span>
                              {t('School Gets', 'School को मिलता है')}
                            </h4>
                            <p className="text-sm text-blue-300" style={{fontFamily: 'Manrope, sans-serif'}}>{f.schoolGets[lang]}</p>
                          </div>
                          <div className="bg-orange-500/10 rounded-2xl p-5 border border-orange-500/20">
                            <h4 className="font-bold text-orange-400 text-sm mb-3 flex items-center gap-2" style={{fontFamily: 'Outfit, sans-serif'}}>
                              <span className="w-6 h-6 bg-orange-500/20 rounded-full flex items-center justify-center text-xs">👨‍👩‍👧</span>
                              {t('Parent Gets', 'Parent को मिलता है')}
                            </h4>
                            <p className="text-sm text-orange-300" style={{fontFamily: 'Manrope, sans-serif'}}>{f.parentGets[lang]}</p>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>

            <div className="text-center mt-12">
              <button onClick={handleActivateOrDemo} className="px-10 py-4 bg-orange-500 text-white rounded-full hover:bg-orange-600 transition-all font-bold text-lg shadow-lg shadow-orange-500/30 transform hover:scale-105 inline-block" style={{fontFamily: 'Manrope, sans-serif'}} data-testid="ae-cta-features">
                {ctaLabel('Start Free Demo', 'Free Demo शुरू करें')} →
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section - Light Background */}
      <section className="py-24 px-4 md:px-8 relative z-10" data-testid="faq-section">
        <div className="max-w-3xl mx-auto">
          <div className="text-center mb-12">
            <span className="inline-block px-4 py-2 bg-slate-200 text-slate-600 rounded-full text-sm font-semibold mb-4 uppercase tracking-wider" style={{fontFamily: 'Manrope, sans-serif'}}>FAQ</span>
            <h2 className="text-3xl md:text-5xl font-black text-slate-900 tracking-tight mb-4" style={{fontFamily: 'Outfit, sans-serif'}}>
              {t('Your Questions, Simple Answers', 'आपके सवाल, सीधे जवाब')}
            </h2>
          </div>
          <div className="space-y-3">
            {faqData.map((faq, idx) => (
              <div key={idx} className="bg-white rounded-2xl border border-slate-200 overflow-hidden shadow-sm hover:shadow-md transition-all" data-testid={`faq-item-${idx}`}>
                <button onClick={() => setOpenFaq(openFaq === idx ? null : idx)} className="w-full p-5 flex items-center justify-between text-left group">
                  <span className="font-bold text-slate-900 pr-4" style={{fontFamily: 'Outfit, sans-serif'}}>{faq.q[lang]}</span>
                  <svg className={`w-5 h-5 text-slate-400 transition-transform flex-shrink-0 ${openFaq === idx ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" /></svg>
                </button>
                {openFaq === idx && (
                  <div className="px-5 pb-5 -mt-1">
                    <p className="text-slate-600 leading-relaxed" style={{fontFamily: 'Manrope, sans-serif'}}>{faq.a[lang]}</p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* School Success Stories - Dark Floating Island */}
      <section className="py-8 px-4 md:px-8 relative z-10" data-testid="success-stories-section">
        <div className="max-w-[95%] xl:max-w-7xl mx-auto">
          <div className="bg-slate-950 rounded-[2.5rem] md:rounded-[3rem] p-8 md:p-12 lg:p-16 shadow-2xl shadow-slate-900/20">
            <div className="text-center mb-10">
              <span className="inline-block px-4 py-2 bg-yellow-500/20 text-yellow-400 rounded-full text-sm font-semibold mb-4 uppercase tracking-wider border border-yellow-500/30" style={{fontFamily: 'Manrope, sans-serif'}}>
                {t('Real Results from Real Schools', 'असली Schools के असली Results')}
              </span>
              <h2 className="text-3xl md:text-5xl font-black text-white mb-4 tracking-tight" style={{fontFamily: 'Outfit, sans-serif'}}>
                {t('See What Other Principals Say', 'दूसरे Principals क्या कहते हैं देखिए')}
              </h2>
              <p className="text-lg text-white/60" style={{fontFamily: 'Manrope, sans-serif'}}>
                {t('Real schools, real principals, real results', 'असली schools, असली principals, असली results')}
              </p>
            </div>

            {/* Main Story Card */}
            <div className="grid lg:grid-cols-5 gap-6 mb-8">
              {/* Video Thumbnail */}
              <div className="lg:col-span-3 relative rounded-2xl overflow-hidden group cursor-pointer" data-testid="story-video-card"
                onClick={() => setStoryPlaying(storyPlaying === storyIdx ? null : storyIdx)}
              >
                <img src={successStories[storyIdx].image} alt={successStories[storyIdx].school} className="w-full h-[300px] md:h-[380px] object-cover" />
                <div className="absolute inset-0 bg-gradient-to-t from-slate-950/90 via-slate-950/30 to-slate-950/20 group-hover:from-slate-950/95 transition-all"></div>

                {/* Play Button */}
                <div className="absolute inset-0 flex items-center justify-center">
                  <div className={`w-20 h-20 rounded-full flex items-center justify-center transition-all shadow-2xl ${storyPlaying === storyIdx ? 'bg-red-500 scale-90' : 'bg-white/20 backdrop-blur-md border-2 border-white/40 group-hover:scale-110 group-hover:bg-white/30'}`}>
                    {storyPlaying === storyIdx ? (
                      <svg className="w-8 h-8 text-white" fill="currentColor" viewBox="0 0 24 24"><path d="M6 4h4v16H6V4zm8 0h4v16h-4V4z"/></svg>
                    ) : (
                      <svg className="w-8 h-8 text-white ml-1" fill="currentColor" viewBox="0 0 24 24"><path d="M8 5v14l11-7z"/></svg>
                    )}
                  </div>
                </div>

                {/* Duration Badge */}
                <div className="absolute top-4 right-4 bg-black/50 backdrop-blur text-white text-xs font-medium px-2.5 py-1 rounded-full" style={{fontFamily: 'Manrope, sans-serif'}}>
                  {successStories[storyIdx].duration}
                </div>

                {/* Metric Badge */}
                <div className="absolute top-4 left-4 bg-gradient-to-r from-orange-500 to-amber-500 text-white px-4 py-2 rounded-xl shadow-lg">
                  <span className="text-2xl font-black block" style={{fontFamily: 'Outfit, sans-serif'}}>{successStories[storyIdx].metric.value}</span>
                  <span className="text-[10px] font-medium" style={{fontFamily: 'Manrope, sans-serif'}}>{successStories[storyIdx].metric.label[lang]}</span>
                </div>

                {/* Playing Animation Overlay */}
                {storyPlaying === storyIdx && (
                  <div className="absolute bottom-4 left-4 right-4 flex items-center gap-2 bg-black/40 backdrop-blur rounded-xl px-4 py-2">
                    <div className="flex items-end gap-0.5 h-4">
                      {[1,2,3,4,5].map(i => (
                        <div key={i} className="w-1 bg-green-400 rounded-full animate-pulse" style={{height: `${Math.random() * 16 + 4}px`, animationDelay: `${i * 0.15}s`, animationDuration: '0.8s'}}></div>
                      ))}
                    </div>
                    <span className="text-white/70 text-xs" style={{fontFamily: 'Manrope, sans-serif'}}>{t('Playing...', 'चल रहा है...')}</span>
                    <div className="flex-1 h-1 bg-white/20 rounded-full overflow-hidden">
                      <div className="h-full bg-green-400 rounded-full animate-[progress_30s_linear_infinite]" style={{width: '45%'}}></div>
                    </div>
                  </div>
                )}

                {/* Bottom Info */}
                <div className="absolute bottom-4 left-4" style={{display: storyPlaying === storyIdx ? 'none' : 'block'}}>
                  <p className="text-white font-bold text-lg" style={{fontFamily: 'Outfit, sans-serif'}}>{successStories[storyIdx].school}</p>
                  <p className="text-white/60 text-sm" style={{fontFamily: 'Manrope, sans-serif'}}>{successStories[storyIdx].city} • {successStories[storyIdx].principal}</p>
                </div>
              </div>

              {/* Story Details */}
              <div className="lg:col-span-2 flex flex-col justify-between">
                {/* Quote */}
                <div className="bg-white/5 rounded-2xl p-6 border border-white/10 mb-4" data-testid="story-quote">
                  <svg className="w-8 h-8 text-orange-400/40 mb-3" fill="currentColor" viewBox="0 0 24 24"><path d="M14.017 21v-7.391c0-5.704 3.731-9.57 8.983-10.609l.995 2.151c-2.432.917-3.995 3.638-3.995 5.849h4v10h-9.983zm-14.017 0v-7.391c0-5.704 3.748-9.57 9-10.609l.996 2.151c-2.433.917-3.996 3.638-3.996 5.849h3.983v10h-9.983z"/></svg>
                  <p className="text-white/90 text-sm leading-relaxed italic mb-4" style={{fontFamily: 'Manrope, sans-serif'}}>
                    {successStories[storyIdx].quote[lang]}
                  </p>
                  <div className="flex items-center gap-2">
                    <div className="w-8 h-8 bg-gradient-to-br from-orange-400 to-green-500 rounded-full flex items-center justify-center text-white text-xs font-bold" style={{fontFamily: 'Outfit, sans-serif'}}>
                      {successStories[storyIdx].principal[0]}
                    </div>
                    <div>
                      <p className="text-white text-sm font-bold" style={{fontFamily: 'Outfit, sans-serif'}}>{successStories[storyIdx].principal}</p>
                      <p className="text-white/40 text-xs" style={{fontFamily: 'Manrope, sans-serif'}}>{successStories[storyIdx].school}</p>
                    </div>
                  </div>
                </div>

                {/* Stats */}
                <div className="grid grid-cols-3 gap-3" data-testid="story-stats">
                  {successStories[storyIdx].stats.map((stat, i) => (
                    <div key={i} className="bg-white/5 rounded-xl p-3 text-center border border-white/10">
                      <p className="text-white font-black text-xl" style={{fontFamily: 'Outfit, sans-serif'}}>{stat.value}</p>
                      <p className="text-white/40 text-[10px]" style={{fontFamily: 'Manrope, sans-serif'}}>{stat.label[lang]}</p>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Carousel Dots + Thumbnails */}
            <div className="flex items-center gap-3 overflow-x-auto pb-2" data-testid="story-carousel">
              {successStories.map((story, idx) => (
                <button
                  key={idx}
                  onClick={() => { setStoryIdx(idx); setStoryPlaying(null); }}
                  className={`flex-shrink-0 rounded-xl overflow-hidden border-2 transition-all ${storyIdx === idx ? 'border-orange-500 scale-105 shadow-lg shadow-orange-500/20' : 'border-white/10 opacity-60 hover:opacity-100'}`}
                  data-testid={`story-thumb-${idx}`}
                >
                  <div className="relative w-28 h-16">
                    <img src={story.image} alt={story.school} className="w-full h-full object-cover" />
                    <div className="absolute inset-0 bg-slate-950/40"></div>
                    <div className="absolute bottom-1 left-1.5">
                      <p className="text-white text-[9px] font-bold leading-tight" style={{fontFamily: 'Outfit, sans-serif'}}>{story.school.split(' ').slice(0,2).join(' ')}</p>
                    </div>
                    {storyIdx === idx && <div className="absolute top-1 right-1 w-2 h-2 bg-orange-500 rounded-full animate-pulse"></div>}
                  </div>
                </button>
              ))}
            </div>

            {/* Auto-advance indicator */}
            <div className="flex justify-center mt-4 gap-1.5">
              {successStories.map((_, idx) => (
                <div key={idx} className={`h-1 rounded-full transition-all duration-300 ${storyIdx === idx ? 'w-8 bg-orange-500' : 'w-2 bg-white/20'}`}></div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Final CTA Section */}
      <section className="py-24 px-4 md:px-8 relative z-10">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl md:text-5xl font-black text-slate-900 tracking-tight mb-4" style={{fontFamily: 'Outfit, sans-serif'}}>
            {t('Ready to Fill All Seats This Year?', 'इस साल सारी Seats भरनी हैं?')}
          </h2>
          <p className="text-slate-500 text-lg mb-10 max-w-2xl mx-auto" style={{fontFamily: 'Manrope, sans-serif'}}>
            {t('8 AI tools, fully automated, ready to start. 500+ schools already seeing results. Free demo — see it working for YOUR school.',
               '8 AI हथियार, fully automated, तुरंत शुरू। 500+ schools को पहले से results मिल रहे हैं। Free demo — अपने SCHOOL के लिए देखिए कैसे काम करेगा।')}
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button onClick={handleActivateOrDemo} className="px-8 py-4 bg-orange-500 text-white rounded-full hover:bg-orange-600 transition-all font-semibold text-lg shadow-xl shadow-orange-500/30 transform hover:scale-105 hover:-translate-y-1" style={{fontFamily: 'Manrope, sans-serif'}} data-testid="ae-cta-bottom">
              {t('Get Free Demo for My School', 'मेरे School के लिए Free Demo दें')} →
            </button>
            <a href="https://wa.me/919183100206?text=Principal%20here.%20Want%20to%20know%20about%20Admission%20Engine." target="_blank" rel="noopener noreferrer" className="px-8 py-4 bg-green-500 text-white rounded-full hover:bg-green-600 transition-all font-semibold text-lg shadow-xl shadow-green-500/30 transform hover:scale-105 hover:-translate-y-1 text-center" style={{fontFamily: 'Manrope, sans-serif'}}>
              {t('WhatsApp Us Directly', 'सीधे WhatsApp करें')}
            </a>
          </div>
        </div>
      </section>

      {/* Floating Testimonial Toast */}
      <div className={`fixed bottom-20 left-4 z-50 transition-all duration-500 ${showToast ? 'translate-x-0 opacity-100' : '-translate-x-full opacity-0'}`} data-testid="testimonial-toast">
        <div className="bg-white rounded-2xl shadow-2xl border border-slate-200 p-4 max-w-xs">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 bg-gradient-to-br from-orange-400 to-green-500 rounded-full flex items-center justify-center text-white text-sm font-bold flex-shrink-0" style={{fontFamily: 'Outfit, sans-serif'}}>
              {testimonials[currentToast].name[0]}
            </div>
            <div>
              <p className="text-slate-900 text-sm font-bold" style={{fontFamily: 'Outfit, sans-serif'}}>{testimonials[currentToast].name}</p>
              <p className="text-slate-400 text-[11px]" style={{fontFamily: 'Manrope, sans-serif'}}>{testimonials[currentToast].city}</p>
              <p className="text-slate-600 text-xs mt-1" style={{fontFamily: 'Manrope, sans-serif'}}>{testimonials[currentToast].text[lang]}</p>
            </div>
          </div>
          <p className="text-[10px] text-slate-300 mt-2" style={{fontFamily: 'Manrope, sans-serif'}}>{t('Just now', 'अभी')}</p>
        </div>
      </div>

      {/* Sticky Mobile CTA */}
      <div className="fixed bottom-0 left-0 right-0 z-40 md:hidden bg-white/95 backdrop-blur-xl border-t border-slate-200 px-4 py-3 flex items-center justify-between" data-testid="sticky-mobile-cta">
        <div>
          <p className="text-slate-900 font-bold text-sm" style={{fontFamily: 'Outfit, sans-serif'}}>Admission Engine</p>
          <p className="text-slate-400 text-[11px]" style={{fontFamily: 'Manrope, sans-serif'}}>{t('Ready to use', 'तुरंत शुरू करें')}</p>
        </div>
        <button onClick={handleActivateOrDemo} className="px-5 py-2.5 bg-orange-500 text-white rounded-full text-sm font-bold hover:bg-orange-600 transition shadow-lg" style={{fontFamily: 'Manrope, sans-serif'}} data-testid="sticky-mobile-cta-btn">
          {t('Free Demo', 'Free Demo')} →
        </button>
      </div>

      {/* Share Bar */}
      <div className="fixed bottom-16 md:bottom-4 left-1/2 -translate-x-1/2 z-40 bg-white/95 backdrop-blur shadow-xl rounded-full border border-slate-200 px-4 py-2 flex items-center gap-2" data-testid="ae-share-bar">
        <span className="text-xs text-slate-500 mr-1 hidden sm:inline" style={{fontFamily: 'Manrope, sans-serif'}}>Share:</span>
        {[
          { href: `https://wa.me/?text=${encodeURIComponent(t('AI Admission Engine - 100% Automated | Shiksha Mitra', 'AI Admission Engine - 100% Automated | शिक्षा मित्र') + ' ' + shareUrl)}`, bg: 'bg-green-500', svg: <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>, tid: 'ae-share-whatsapp' },
          { href: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}`, bg: 'bg-[#1877F2]', svg: <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>, tid: 'ae-share-facebook' },
          { href: `https://twitter.com/intent/tweet?text=${encodeURIComponent(t('AI Admission Engine', 'AI Admission Engine'))}&url=${encodeURIComponent(shareUrl)}`, bg: 'bg-black', svg: <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>, tid: 'ae-share-twitter' },
        ].map((s, i) => (
          <a key={i} href={s.href} target="_blank" rel="noopener noreferrer" className={`w-8 h-8 rounded-full ${s.bg} text-white flex items-center justify-center hover:scale-110 transition`} data-testid={s.tid}>
            <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">{s.svg}</svg>
          </a>
        ))}
      </div>

      {/* Footer - Same as Main */}
      <footer className="bg-slate-950 text-white py-10 relative z-10" data-testid="ae-footer">
        <div className="max-w-7xl mx-auto px-4 flex flex-col md:flex-row justify-between items-center gap-4">
          <div>
            <div className="flex items-center gap-2 mb-2">
              <img src={LOGO_URL} alt="Shiksha Mitra Logo" className="h-10 w-10 rounded-lg object-cover" />
              <span className="text-xl font-bold" style={{fontFamily: 'Outfit, sans-serif'}}>शिक्षा मित्र</span>
            </div>
            <p className="text-sm text-slate-400" style={{fontFamily: 'Manrope, sans-serif'}}>AI-Powered School Management</p>
          </div>
          <div className="flex gap-6 text-sm text-slate-400" style={{fontFamily: 'Manrope, sans-serif'}}>
            <Link to="/features" className="hover:text-white transition">Features</Link>
            <Link to="/" className="hover:text-white transition">Home</Link>
            {!isLoggedIn && <Link to="/login" className="hover:text-white transition">Login</Link>}
          </div>
        </div>
        <p className="text-center text-xs text-slate-600 mt-6" style={{fontFamily: 'Manrope, sans-serif'}}>© 2025 System Of Success. All rights reserved.</p>
      </footer>

      {/* Demo Booking Modal */}
      <DemoBookingModal isOpen={showDemoModal} onClose={() => setShowDemoModal(false)} source="admission_engine_page" />
    </div>
  );
}
